<template>
  <div class="p-3 plan-tab-shell">
    <div class="d-flex align-items-center justify-content-between mb-3 px-2 plan-tab-toolbar gap-2 flex-wrap">
      <div class="d-flex align-items-center gap-2 flex-wrap">
      <div class="input-group input-group-sm" style="width:200px;">
        <span class="input-group-text bg-white border-end-0">
          <i class="fas fa-search text-secondary" style="font-size:0.7rem;"></i>
        </span>
        <input v-model="search" type="text" class="form-control border-start-0 ps-0"
          placeholder="Search test run..." style="font-size:0.75rem;" />
      </div>
      <template v-if="isHistoryTeamTab">
        <div class="history-filter-field">
          <base-select
            size="sm"
            :model-value="historyTeamFilter"
            :options="historyTeamOptions"
            option-label="label"
            option-value="value"
            @update:model-value="(v) => { historyTeamFilter = v ?? ''; }"
          />
        </div>
        <div class="history-filter-field">
          <base-select
            size="sm"
            searchable
            :model-value="historyQaFilter"
            :options="historyQaOptions"
            option-label="label"
            option-value="value"
            @update:model-value="(v) => { historyQaFilter = v ?? ''; }"
          />
        </div>
      </template>
      </div>
      <div class="d-flex align-items-center gap-2">
        <button
          v-if="!isHistoryTeamTab"
          class="btn btn-sm btn-outline-secondary mb-0"
          style="font-size:0.8rem;"
          @click="openCreateModal"
        >
          <i class="fas fa-plus me-1"></i> New Test Run
        </button>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary mb-0"
          style="font-size:0.8rem;"
          :disabled="!(filteredRuns && filteredRuns.length)"
          @click="openCopyModal(selectedRun)"
        >
          <i class="fas fa-clone me-1"></i> Copy
        </button>
      </div>
    </div>

    <!-- Table -->
    <div class="px-2 plan-tab-table-wrap">
      <div v-if="loading" class="text-center py-5">
        <i class="fas fa-spinner fa-spin fa-lg text-secondary"></i>
        <p class="text-secondary text-sm mt-2 mb-0">Loading...</p>
      </div>
      <div v-else class="run-table plan-tab-table">
        <div class="run-table-header plan-tab-table-header">
          <span class="col-name">TEST RUN NAME</span>
          <span class="col-env text-center">ENVIRONMENT</span>
          <span class="col-date text-center">START DATE</span>
          <span class="col-date text-center">DUE DATE</span>
          <span class="col-total text-center">TOTAL</span>
          <span class="col-status text-center">STATUS</span>
          <span class="col-progress text-center">PROGRESS</span>
          <span class="col-action text-center">ACTIONS</span>
        </div>
        <div class="plan-tab-table-body">
        <div v-if="!groupedFilteredRuns.length" class="pm-empty-table-row">
          <i class="fas fa-inbox fa-2x text-secondary opacity-5"></i>
          <p class="text-secondary text-sm mt-2 mb-0">{{ emptyRunsMessage }}</p>
        </div>
        <template v-for="subGroup in groupedFilteredRuns" :key="subGroup.key">
          <div class="run-folder-row run-folder-row--sub-project" @click="toggleRunGroup(subGroup.key)">
            <i
              class="fas run-folder-chevron me-1"
              :class="collapsedRunGroups.includes(subGroup.key) ? 'fa-chevron-right' : 'fa-chevron-down'"
            ></i>
            <i
              class="fas me-2 run-folder-icon run-folder-icon--sub-project"
              :class="collapsedRunGroups.includes(subGroup.key) ? 'fa-folder' : 'fa-folder-open'"
            ></i>
            <span class="run-folder-label">{{ subGroup.label }}</span>
            <span class="run-folder-count">({{ subGroup.count }})</span>
          </div>
          <template v-if="!collapsedRunGroups.includes(subGroup.key)">
            <template v-for="planGroup in subGroup.plans" :key="planGroup.key">
              <div
                class="run-folder-row run-folder-row--plan"
                @click="toggleRunPlan(subGroup.key, planGroup.key)"
              >
                <i
                  class="fas run-folder-chevron me-1"
                  :class="isRunPlanCollapsed(subGroup.key, planGroup.key) ? 'fa-chevron-right' : 'fa-chevron-down'"
                ></i>
                <i
                  class="fas me-2 run-folder-icon run-folder-icon--plan"
                  :class="isRunPlanCollapsed(subGroup.key, planGroup.key) ? 'fa-folder' : 'fa-folder-open'"
                ></i>
                <span class="run-folder-label">{{ planGroup.label }}</span>
                <span class="run-folder-count">({{ planGroup.runs.length }})</span>
              </div>
              <template v-if="!isRunPlanCollapsed(subGroup.key, planGroup.key)">
        <div
          v-for="run in planGroup.runs"
          :key="run.id"
          class="run-row"
          :class="{ 'run-row--selected': String(selectedRunId) === String(run.id) }"
          @click="selectRun(run)"
        >
          <div class="col-name">
            <div class="run-name-line">
              <button type="button" class="run-name-btn mb-0" @click.stop="openDetailPage(run)">{{ run.name }}</button>
              <span v-if="isRunOverdue(run)" class="run-overdue-label">over due</span>
            </div>
            <p v-if="run.description" class="run-sub mb-0">{{ run.description }}</p>
            <div v-if="assigneeList(run).length > 0" class="d-flex flex-wrap gap-1 mt-1">
              <span v-for="name in assigneeList(run)" :key="name" class="qa-badge bo-assignee-pill">{{ name }}</span>
            </div>
          </div>
          <div class="col-env text-center">
            <span class="text-secondary" style="font-size:0.8rem !important;">{{ run.environment || "—" }}</span>
          </div>
          <div class="col-date text-center">
            <span class="text-xs text-secondary">{{ formatDateDisplay(run.startDate) }}</span>
          </div>
          <div class="col-date text-center">
            <span class="text-xs text-secondary">{{ formatDateDisplay(run.dueDate) }}</span>
          </div>
          <div class="col-total text-center">
            <span class="text-sm font-weight-bold">{{ run.total || 0 }}</span>
          </div>
          <div class="col-status text-center">
            <span class="status-pill" :class="statusPillClass(run.status)">{{ displayRunStatus(run.status) }}</span>
          </div>
          <div class="col-progress">
            <div class="d-flex align-items-center justify-content-center gap-2">
              <span class="text-xs font-weight-bold" style="min-width:30px;">{{ runProgress(run) }}%</span>
              <div style="flex:1; max-width:70px;">
                <soft-progress :color="progressColor(runProgress(run))" variant="gradient" :percentage="runProgress(run)" />
              </div>
            </div>
          </div>
          <div class="col-action text-center" @click.stop>
            <button class="btn btn-link p-0 action-edit" title="Edit" @click="openEditModal(run)">
              <i class="fas fa-edit"></i>
            </button>
            <button class="btn btn-link p-0 action-delete" title="Delete" @click="deleteRun(run)">
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>
        </div>
              </template>
            </template>
          </template>
        </template>
        </div>
      </div>
    </div>

    <!-- Create / Edit Test Run Modal -->
    <Teleport to="body">
      <div v-if="showCreateModal" class="modal-overlay test-run-modal-overlay">
        <div class="modal-box test-run-modal">
          <div class="test-run-modal-header d-flex align-items-center justify-content-between p-3 border-bottom">
            <h6 class="mb-0 font-weight-bold">{{ editMode ? 'Edit Test Run' : 'Create Test Run' }}</h6>
          </div>
          <div class="test-run-modal-body p-4">

            <div v-if="copyMode" class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block" :class="{ 'text-danger': !!formFieldErrors.copySource }">
                Copy from Test Run <span class="text-danger">*</span>
              </label>
              <base-select
                size="sm"
                :model-value="copySourceRunId || null"
                :options="copyRunSelectOptions"
                option-label="label"
                option-value="value"
                placeholder="— Select test run —"
                :has-error="!!formFieldErrors.copySource"
                @update:model-value="onCopySourceChange"
              />
              <p v-if="formFieldErrors.copySource" class="text-xs text-danger mb-0 mt-1">{{ formFieldErrors.copySource }}</p>
            </div>

            <div v-if="standalone" class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Plan <span class="text-secondary font-weight-normal">(optional)</span></label>
              <base-select
                size="sm"
                :model-value="form.projectPlanId || null"
                :options="planSelectOptions"
                option-label="label"
                option-value="value"
                placeholder="— Bind to a plan later —"
                :disabled="copyMode"
                @update:model-value="onStandalonePlanChange"
              />
            </div>

            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block" :class="{ 'text-danger': !!formFieldErrors.casesSource || !!formFieldErrors.cases }">
                Test cases <span class="text-danger">*</span>
                <span class="text-secondary font-weight-normal">(any project / group / folder)</span>
              </label>
              <div class="row g-2 align-items-end">
                <div class="col-4">
                  <label class="text-xs mb-1 d-block" :class="formFieldErrors.casesSource ? 'text-danger' : 'text-secondary'">Project <span class="text-danger">*</span></label>
                  <base-select
                    size="sm"
                    :model-value="catalog.projectId || null"
                    :options="catalogProjectOptions"
                    option-label="label"
                    option-value="value"
                    placeholder="— Select project —"
                    :disabled="copyMode || catalogProjectLocked"
                    :has-error="!!formFieldErrors.casesSource"
                    @update:model-value="onCatalogProjectChange"
                  />
                </div>
                <div class="col-3">
                  <label class="text-xs text-secondary mb-1 d-block">Group</label>
                  <base-select
                    size="sm"
                    :model-value="catalog.functionId || null"
                    :options="catalogFunctionOptions"
                    option-label="label"
                    option-value="value"
                    placeholder="— All groups —"
                    :disabled="copyMode || !catalog.projectId"
                    @update:model-value="onCatalogFunctionChange"
                  />
                </div>
                <div class="col-3">
                  <label class="text-xs text-secondary mb-1 d-block">Folder</label>
                  <base-select
                    size="sm"
                    :model-value="catalog.folderId || null"
                    :options="catalogFolderOptions"
                    option-label="label"
                    option-value="value"
                    placeholder="— All folders —"
                    :disabled="copyMode || !catalog.functionId"
                    @update:model-value="(v) => { catalog.folderId = v ? String(v) : ''; }"
                  />
                </div>
                <div class="col-2">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary mb-0 w-100"
                    style="font-size:0.73rem;"
                    :disabled="copyMode || !catalog.projectId || catalogLoading"
                    @click="addCatalogSource"
                  >
                    <span v-if="catalogLoading"><i class="fas fa-spinner fa-spin me-1"></i>Loading</span>
                    <span v-else><i class="fas fa-plus me-1"></i> Add</span>
                  </button>
                </div>
              </div>
              <div v-if="caseSources.length" class="d-flex flex-wrap gap-1 mt-2">
                <span v-for="src in caseSources" :key="src.key" class="qa-badge">
                  {{ src.label }}
                  <span v-if="!copyMode" class="ms-1" style="cursor:pointer;" @click.stop="removeCatalogSource(src.key)">×</span>
                </span>
              </div>
              <p v-if="formFieldErrors.casesSource" class="text-xs text-danger mb-0 mt-2">{{ formFieldErrors.casesSource }}</p>
              <p v-else class="text-secondary text-xs mb-0 mt-2">
                {{ catalogProjectLocked
                  ? "Next adds stay on this project. You can change Group and Folder."
                  : "Pick a project, then add any groups and folders. After the first add, the project is locked." }}
              </p>
            </div>

            <!-- Row 1: Test Run Name / Status / Assign Run -->
            <div class="row mb-3">
              <div class="col-4">
                <label class="text-xs font-weight-bold mb-1 d-block" :class="{ 'text-danger': !!formFieldErrors.name }">Test Run Name <span class="text-danger">*</span></label>
                <input
                  v-model="form.name"
                  type="text"
                  class="form-control form-control-sm"
                  :class="{ 'is-invalid': !!formFieldErrors.name }"
                  placeholder="Enter test run name"
                  style="font-size:0.8rem;"
                  @input="clearFieldError('name')"
                />
                <p v-if="formFieldErrors.name" class="text-xs text-danger mb-0 mt-1">{{ formFieldErrors.name }}</p>
              </div>
              <div class="col-4">
                <label class="text-xs font-weight-bold mb-1 d-block">Status</label>
                <base-select
                  v-model="form.state"
                  size="sm"
                  :options="runStatusOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="— Status —"
                  :disabled="copyMode"
                />
              </div>
              <div class="col-4">
                <label class="text-xs font-weight-bold mb-1 d-block">Assign Run</label>
                <div class="position-relative" ref="assigneeDropdownRef">
                  <div class="form-control form-control-sm d-flex flex-wrap gap-1 align-items-center"
                    :style="{ minHeight: '31px', cursor: copyMode ? 'default' : 'pointer' }"
                    @click="copyMode ? null : (showAssigneeDropdown = !showAssigneeDropdown)">
                    <span v-if="form.userIds.length === 0" class="text-secondary text-xs">— Select tester —</span>
                    <span v-for="uid in form.userIds" :key="uid" class="qa-badge">
                      {{ userNameById(uid) }}
                      <span v-if="!copyMode" class="ms-1" style="cursor:pointer;" @click.stop="removeUser(uid)">×</span>
                    </span>
                  </div>
                  <div v-if="showAssigneeDropdown && !copyMode">
                    class="border rounded bg-white shadow-sm position-absolute w-100"
                    style="z-index:9999; max-height:180px; overflow-y:auto; top:100%;">
                    <div v-for="u in users" :key="u.userId"
                      class="px-3 py-2 d-flex align-items-center gap-2"
                      style="cursor:pointer; font-size:0.8rem;"
                      :style="form.userIds.includes(Number(u.userId)) ? 'background:#f0f9ff;' : ''"
                      @click.stop="toggleUser(Number(u.userId))">
                      <i class="fas text-xs me-1"
                        :class="form.userIds.includes(Number(u.userId)) ? 'fa-check-square text-info' : 'fa-square text-secondary'"></i>
                      {{ formatUserDisplayName(u) }}
                    </div>
                    <div v-if="users.length === 0" class="px-3 py-2 text-secondary text-xs">No users found</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Row 2: Stage / Environment / Module / Version -->
            <div class="row mb-3">
              <div class="col-3">
                <label class="text-xs font-weight-bold mb-1 d-block" :class="{ 'text-danger': !!formFieldErrors.stage }">Stage <span class="text-danger">*</span></label>
                <base-select
                  v-model="form.stage"
                  size="sm"
                  :options="stageOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="-- Select stage --"
                  :has-error="!!formFieldErrors.stage"
                  :disabled="copyMode"
                  @update:model-value="clearFieldError('stage')"
                />
                <p v-if="formFieldErrors.stage" class="text-xs text-danger mb-0 mt-1">{{ formFieldErrors.stage }}</p>
              </div>
              <div class="col-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Environment</label>
                <base-select
                  v-model="form.environment"
                  size="sm"
                  :options="environmentOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="-- Select --"
                  :disabled="copyMode"
                />
              </div>
              <div class="col-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Module</label>
                <div class="position-relative" ref="configDropdownRef">
                  <div class="form-control form-control-sm d-flex flex-wrap gap-1 align-items-center"
                    :style="{ minHeight: '31px', cursor: copyMode ? 'default' : 'pointer' }"
                    @click="copyMode ? null : (showConfigDropdown = !showConfigDropdown)">
                    <span v-if="form.configurations.length === 0" class="text-secondary text-xs">— Select —</span>
                    <span v-for="c in form.configurations" :key="c" class="config-badge">
                      {{ c }}
                      <span v-if="!copyMode" class="ms-1" style="cursor:pointer;" @click.stop="removeConfig(c)">×</span>
                    </span>
                  </div>
                  <div v-if="showConfigDropdown && !copyMode">
                    class="border rounded bg-white shadow-sm position-absolute w-100"
                    style="z-index:9999; max-height:180px; overflow-y:auto; top:100%;">
                    <div v-for="c in configOptions" :key="c"
                      class="px-3 py-2 d-flex align-items-center gap-2"
                      style="cursor:pointer; font-size:0.8rem;"
                      :style="form.configurations.includes(c) ? 'background:#f0f9ff;' : ''"
                      @click.stop="toggleConfig(c)">
                      <i class="fas text-xs me-1"
                        :class="form.configurations.includes(c) ? 'fa-check-square text-info' : 'fa-square text-secondary'"></i>
                      {{ c }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Version</label>
                <input v-model="form.version" type="text" class="form-control form-control-sm"
                  placeholder="e.g. v1.0.0" style="font-size:0.8rem;" :disabled="copyMode" />
              </div>
            </div>

            <!-- Row 3: Description -->
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Description</label>
              <textarea v-model="form.description" class="form-control form-control-sm" rows="2"
                placeholder="Enter description..." style="font-size:0.8rem;" :disabled="copyMode"></textarea>
            </div>

            <!-- Summary: global dates + computed days/cases -->
            <div class="row mb-3">
              <div class="col-3">
                <div class="summary-card summary-card-editable">
                  <label class="summary-label mb-1 d-block">Start Date</label>
                  <input v-model="form.runStartDate" type="date" class="form-control form-control-sm summary-date-input" :disabled="copyMode" />
                </div>
              </div>
              <div class="col-3">
                <div class="summary-card summary-card-editable">
                  <label class="summary-label mb-1 d-block">Due Date</label>
                  <input v-model="form.runDueDate" type="date" class="form-control form-control-sm summary-date-input" :disabled="copyMode" />
                </div>
              </div>
              <div class="col-3">
                <div class="summary-card">
                  <p class="summary-label">Days <span class="summary-hint">(working days)</span></p>
                  <p class="summary-value">{{ summaryDisplay.days }}</p>
                </div>
              </div>
              <div class="col-3">
                <div class="summary-card">
                  <p class="summary-label">Cases</p>
                  <p class="summary-value">{{ summaryDisplay.cases }}</p>
                </div>
              </div>
            </div>

            <!-- Test Case Selection -->
            <div class="mb-3" :class="{ 'run-cases-required-error': !!formFieldErrors.cases }">
              <!-- Box 1: By Scenario -->
              <div class="mb-3">
                <div class="selection-section" :class="{ 'selection-section--error': !!formFieldErrors.cases }">
                  <p class="sel-box-title">By Scenario</p>
                  <div v-if="scenarioRowsForCategory.length > 0">
                    <div class="sel-header">
                      <span class="sc-col-1">Scenario</span>
                      <span class="sc-col-2">Sub-Scenario</span>
                      <span class="sel-col-count text-center">Cases</span>
                      <span class="sel-col-del"></span>
                    </div>
                    <div v-for="row in scenarioRowsForCategory" :key="row._id" class="sel-row">
                      <div class="sc-col-1">
                        <base-select
                          v-model="row.scenarioId"
                          size="sm"
                          :options="scenarioSelectOptions"
                          option-label="label"
                          option-value="value"
                          placeholder="-- Scenario --"
                          :disabled="copyMode || row.isExisting"
                          @change="onScenarioRowScenarioChange(row)"
                        />
                      </div>
                      <div class="sc-col-2">
                        <base-select
                          v-model="row.subScenarioId"
                          size="sm"
                          :options="subScenarioSelectOptionsForScenarioRow(row)"
                          option-label="label"
                          option-value="value"
                          placeholder="-- Sub --"
                          :disabled="copyMode || row.isExisting || !row.scenarioId"
                          @change="row.lockedTestCaseIds = []"
                        />
                      </div>
                      <div class="sel-col-count text-center">
                        <span class="text-xs font-weight-bold text-secondary">{{ caseCountForScenarioRow(row) }}</span>
                      </div>
                      <div class="sel-col-del text-center">
                        <button v-if="canDeleteRow(row)" type="button" class="btn btn-link btn-sm p-0 action-delete"
                          @click="removeScenarioRow(row._id)">
                          <i class="fas fa-times" style="font-size:0.8rem;"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-center py-3 text-secondary" style="font-size:0.75rem;">
                    {{ copyMode
                      ? (copySourceRunId ? "No scenarios in this test run." : "Select a test run to copy.")
                      : 'Click "+ Add Row" to select a scenario.' }}
                  </div>
                  <div v-if="!copyMode" class="d-flex align-items-center gap-2 mt-2">
                    <button type="button" class="btn btn-sm btn-outline-secondary mb-0" style="font-size:0.73rem;"
                      @click="form.scenarioRows.push(blankScenarioRow())">
                      <i class="fas fa-plus me-1"></i> Add Row
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-primary mb-0" style="font-size:0.73rem;"
                      :disabled="!canAddAllScenarios"
                      title="Add all scenarios"
                      @click="addAllScenariosForCategory">
                      <i class="fas fa-list me-1"></i> All
                    </button>
                  </div>
                </div>
              </div>

              <!-- Box 2: By Test Case -->
              <div>
                <div class="selection-section" :class="{ 'selection-section--error': !!formFieldErrors.cases }">
                  <p class="sel-box-title">By Test Case</p>
                  <div v-if="tcRowsForCategory.length > 0">
                    <div class="sel-header">
                      <span class="tc-col-1">Scenario</span>
                      <span class="tc-col-2">Sub-Scenario</span>
                      <span class="tc-col-3">Test Case</span>
                      <span class="sel-col-del"></span>
                    </div>
                    <div v-for="row in tcRowsForCategory" :key="row._id" class="sel-row">
                      <div class="tc-col-1">
                        <base-select
                          v-model="row.scenarioId"
                          size="sm"
                          :options="scenarioSelectOptions"
                          option-label="label"
                          option-value="value"
                          placeholder="-- Scenario --"
                          :disabled="copyMode || row.isExisting"
                          @change="onTcRowScenarioChange(row)"
                        />
                      </div>
                      <div class="tc-col-2">
                        <base-select
                          v-model="row.subScenarioId"
                          size="sm"
                          :options="subScenarioSelectOptionsForTcRow(row)"
                          option-label="label"
                          option-value="value"
                          placeholder="-- Sub --"
                          :disabled="copyMode || row.isExisting || !row.scenarioId"
                          @change="row.testCaseId = ''"
                        />
                      </div>
                      <div class="tc-col-3">
                        <base-select
                          v-model="row.testCaseId"
                          size="sm"
                          :options="testCaseSelectOptionsForRow(row)"
                          option-label="label"
                          option-value="value"
                          placeholder="-- Test Case --"
                          :disabled="copyMode || row.isExisting || !row.subScenarioId"
                        />
                      </div>
                      <div class="sel-col-del text-center">
                        <button v-if="canDeleteRow(row)" type="button" class="btn btn-link btn-sm p-0 action-delete"
                          @click="removeTcRow(row._id)">
                          <i class="fas fa-times" style="font-size:0.8rem;"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-center py-3 text-secondary" style="font-size:0.75rem;">
                    {{ copyMode
                      ? (copySourceRunId ? "No test cases in this test run." : "Select a test run to copy.")
                      : 'Click "+ Add Row" to select a test case.' }}
                  </div>
                  <button v-if="!copyMode" type="button" class="btn btn-sm btn-outline-secondary mt-2 mb-0" style="font-size:0.73rem;"
                    @click="form.tcRows.push(blankTcRow())">
                    <i class="fas fa-plus me-1"></i> Add Row
                  </button>
                </div>
              </div>
              <p v-if="formFieldErrors.cases" class="text-xs text-danger mb-0 mt-2">{{ formFieldErrors.cases }}</p>
            </div>

          </div>

          <div class="test-run-modal-footer d-flex align-items-center justify-content-end gap-2 p-3 border-top">
            <div v-if="saveRunError" class="alert alert-danger py-2 px-3 mb-0 me-auto text-xs">{{ saveRunError }}</div>
            <button class="btn btn-sm btn-outline-secondary mb-0" :disabled="savingRun" @click="showCreateModal = false">Cancel</button>
            <button class="btn btn-sm bg-gradient-dark text-white mb-0" :disabled="savingRun" @click="saveRun">
              <span v-if="savingRun"><i class="fas fa-spinner fa-spin me-1"></i>{{ savingRunLabel }}</span>
              <span v-else>{{ editMode ? 'Save' : 'Create' }}</span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="showRunView" class="qa-tc-review-overlay qa-tr-view-overlay" :class="runViewMode === 'pdf' ? 'is-pdf' : 'is-standard'">
        <div class="qa-tc-review-shell">
          <div class="qa-tc-review-header">
            <div class="qa-tc-review-header-title">
              <h6 class="mb-0 font-weight-bold">TEST RUN : {{ viewingRun?.name || "—" }}</h6>
              <p class="text-xs text-secondary mb-0">{{ viewingRun?.subProjectName || "—" }}</p>
            </div>
            <div class="d-flex align-items-center gap-2">
              <div class="qa-tc-review-mode" role="tablist" aria-label="View mode">
                <button
                  type="button"
                  class="qa-tc-review-mode-btn"
                  :class="{ active: runViewMode === 'standard' }"
                  @click="runViewMode = 'standard'"
                >Standard</button>
                <button
                  type="button"
                  class="qa-tc-review-mode-btn"
                  :class="{ active: runViewMode === 'pdf' }"
                  @click="runViewMode = 'pdf'"
                >Table</button>
              </div>
              <button type="button" class="btn btn-sm btn-secondary mb-0" @click="closeRunView">Close</button>
            </div>
          </div>
          <div class="qa-tc-review-filters">
            <div class="qa-tc-review-filter-field qa-tc-review-filter-field--search">
              <label>Search</label>
              <div class="input-group input-group-sm">
                <span class="input-group-text bg-white border-end-0">
                  <i class="fas fa-search text-secondary" style="font-size:0.7rem;"></i>
                </span>
                <input
                  v-model="runViewFilters.search"
                  type="search"
                  class="form-control border-start-0 ps-0"
                  placeholder="Search test case..."
                  style="font-size:0.75rem;"
                />
              </div>
            </div>
            <div class="qa-tc-review-filter-field">
              <label>Type</label>
              <base-select
                size="sm"
                :model-value="runViewFilters.testType"
                :options="runViewTypeFilterOptions"
                option-label="label"
                option-value="value"
                @update:model-value="(v) => setRunViewFilter('testType', v ?? '')"
              />
            </div>
            <div class="qa-tc-review-filter-field">
              <label>Priority</label>
              <base-select
                size="sm"
                :model-value="runViewFilters.priority"
                :options="runViewPriorityFilterOptions"
                option-label="label"
                option-value="value"
                @update:model-value="(v) => setRunViewFilter('priority', v ?? '')"
              />
            </div>
            <div class="qa-tc-review-filter-field">
              <label>Scenario</label>
              <base-select
                size="sm"
                searchable
                :model-value="runViewFilters.scenario"
                :options="runViewScenarioFilterOptions"
                option-label="label"
                option-value="value"
                @update:model-value="onRunViewScenarioFilterChange"
              />
            </div>
            <div class="qa-tc-review-filter-field">
              <label>Sub-Scenario</label>
              <base-select
                size="sm"
                searchable
                :model-value="runViewFilters.subScenario"
                :options="runViewSubScenarioFilterOptions"
                option-label="label"
                option-value="value"
                @update:model-value="(v) => setRunViewFilter('subScenario', v ?? '')"
              />
            </div>
            <div class="qa-tc-review-filter-field">
              <label>Status</label>
              <base-select
                size="sm"
                :model-value="runViewFilters.status"
                :options="runViewStatusFilterOptions"
                option-label="label"
                option-value="value"
                @update:model-value="(v) => setRunViewFilter('status', v ?? '')"
              />
            </div>
            <div class="qa-tc-review-filter-field qa-tc-review-filter-field--clear">
              <label class="qa-tc-review-filter-clear-label">&nbsp;</label>
              <button type="button" class="btn btn-sm btn-outline-secondary mb-0 qa-tr-view-clear-btn" @click="clearRunViewFilters">
                Clear Filters
              </button>
            </div>
          </div>
          <div class="qa-tc-review-body">
            <div class="qa-tc-review-doc">
              <div v-if="runViewLoading" class="qa-tc-review-status">
                <i class="fas fa-spinner fa-spin me-2"></i> Loading test cases...
              </div>
              <div v-else-if="runViewError" class="qa-tc-review-status text-danger">{{ runViewError }}</div>
              <div v-else class="qa-tc-review-content" :class="{ 'qa-tc-review-content--pdf': runViewMode === 'pdf' }">
                <div class="qa-tc-review-title-block">
                  <div class="qa-tc-review-info-grid">
                    <div>
                      <span>Status</span>
                      <strong class="qa-tr-view-result" :style="{ color: viewStatusColor(viewingRun?.status) }">{{ displayRunStatus(viewingRun?.status) }}</strong>
                    </div>
                    <div>
                      <span>Result</span>
                      <strong class="qa-tr-view-result" :style="{ color: viewResultColor(viewingRun?.result) }">{{ displayRunResult(viewingRun?.result) }}</strong>
                    </div>
                    <div>
                      <span>Test Case total</span>
                      <strong>{{ runViewFilteredCases.length }}</strong>
                    </div>
                  </div>
                </div>

                <div v-if="runViewMode === 'pdf'" class="qa-tc-pdf-wrap">
                  <table class="qa-tc-pdf-table qa-tr-view-table">
                    <colgroup>
                      <col class="qa-tr-view-col-id" />
                      <col class="qa-tr-view-col-name" />
                      <col class="qa-tr-view-col-type" />
                      <col class="qa-tr-view-col-priority" />
                      <col class="qa-tr-view-col-step" />
                      <col class="qa-tr-view-col-expected" />
                      <col class="qa-tr-view-col-actual" />
                      <col class="qa-tr-view-col-result" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Test Case Name</th>
                        <th>Test Type</th>
                        <th>Priority</th>
                        <th>Test Step</th>
                        <th>Expected Result</th>
                        <th>Actual Result</th>
                        <th>Result</th>
                      </tr>
                    </thead>
                    <tbody v-if="!runViewFilteredCases.length">
                      <tr>
                        <td colspan="8" class="qa-tc-review-status">No test cases match this filter.</td>
                      </tr>
                    </tbody>
                    <tbody v-for="scGroup in runViewGroupedCases" :key="scGroup.scenario">
                      <tr class="qa-tc-pdf-section qa-tc-pdf-section--sc">
                        <td colspan="8">Scenario: {{ scGroup.scenario || "(No Scenario)" }}</td>
                      </tr>
                      <template v-for="subGroup in scGroup.subGroups" :key="`${scGroup.scenario}|${subGroup.subScenario}`">
                        <tr class="qa-tc-pdf-section qa-tc-pdf-section--sub">
                          <td colspan="8">Sub-Scenario: {{ subGroup.subScenario || "(No Sub-Scenario)" }}</td>
                        </tr>
                        <template v-for="tc in subGroup.cases" :key="tc.key">
                          <tr class="qa-tc-pdf-case">
                            <td class="qa-tc-pdf-id" :rowspan="viewStepCount(tc)">{{ tc.tcLabel }}</td>
                            <td class="qa-tc-pdf-name" :rowspan="viewStepCount(tc)">
                              <BulletText :text="tc.title" list-class="mb-0" fallback="Untitled" />
                            </td>
                            <td class="text-center" :rowspan="viewStepCount(tc)">{{ tc.testType || tc.typeOfTestCase || "—" }}</td>
                            <td class="text-center" :rowspan="viewStepCount(tc)">{{ tc.priority || "—" }}</td>
                            <td class="qa-tc-pdf-pre"><BulletText :text="viewStepText(tc, 0)" list-class="mb-0" fallback="—" /></td>
                            <td class="qa-tc-pdf-pre"><BulletText :text="viewExpectedText(tc, 0)" list-class="mb-0" fallback="—" /></td>
                            <td class="qa-tc-pdf-pre" :rowspan="viewActualIsByStep(tc) ? 1 : viewStepCount(tc)">
                              <BulletText :text="viewActualText(tc, 0)" list-class="mb-0" fallback="—" />
                              <div v-if="viewStepActualMediaPreview(tc, 0).length" class="qa-tr-view-media">
                                <button
                                  v-for="item in viewStepActualMediaPreview(tc, 0)"
                                  :key="item.index"
                                  type="button"
                                  class="qa-tr-view-media-thumb"
                                  @click="openViewStepMedia(tc, 0, item.index)"
                                >
                                  <video v-if="isVideoMediaUrl(item.src)" :src="item.src" muted preload="metadata" playsinline />
                                  <img v-else :src="item.src" alt="" referrerpolicy="no-referrer" />
                                  <span v-if="isVideoMediaUrl(item.src) && !item.extra" class="qa-tr-view-media-play">
                                    <i class="fas fa-play"></i>
                                  </span>
                                  <span v-if="item.extra" class="qa-tr-view-media-more">+{{ item.extra }}</span>
                                </button>
                              </div>
                            </td>
                            <td class="text-center" :rowspan="viewStepCount(tc)">
                              <strong class="qa-tr-view-result" :style="{ color: viewResultColor(tc.runStatus) }">{{ displayRunResult(tc.runStatus) }}</strong>
                            </td>
                          </tr>
                          <tr
                            v-for="(step, idx) in viewExtraSteps(tc)"
                            :key="`${tc.key}-step-${idx + 1}`"
                            class="qa-tc-pdf-case qa-tc-pdf-case--cont"
                          >
                            <td class="qa-tc-pdf-pre"><BulletText :text="viewStepText(tc, idx + 1)" list-class="mb-0" fallback="—" /></td>
                            <td class="qa-tc-pdf-pre"><BulletText :text="viewExpectedText(tc, idx + 1)" list-class="mb-0" fallback="—" /></td>
                            <td v-if="viewActualIsByStep(tc)" class="qa-tc-pdf-pre">
                              <BulletText :text="viewActualText(tc, idx + 1)" list-class="mb-0" fallback="—" />
                              <div v-if="viewStepActualMediaPreview(tc, idx + 1).length" class="qa-tr-view-media">
                                <button
                                  v-for="item in viewStepActualMediaPreview(tc, idx + 1)"
                                  :key="item.index"
                                  type="button"
                                  class="qa-tr-view-media-thumb"
                                  @click="openViewStepMedia(tc, idx + 1, item.index)"
                                >
                                  <video v-if="isVideoMediaUrl(item.src)" :src="item.src" muted preload="metadata" playsinline />
                                  <img v-else :src="item.src" alt="" referrerpolicy="no-referrer" />
                                  <span v-if="isVideoMediaUrl(item.src) && !item.extra" class="qa-tr-view-media-play">
                                    <i class="fas fa-play"></i>
                                  </span>
                                  <span v-if="item.extra" class="qa-tr-view-media-more">+{{ item.extra }}</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        </template>
                      </template>
                    </tbody>
                  </table>
                </div>

                <template v-else>
                  <p v-if="!runViewFilteredCases.length" class="qa-tc-review-status">No test cases match this filter.</p>
                  <article v-for="tc in runViewFilteredCases" :key="tc.key" class="qa-tc-review-case">
                    <div class="qa-tc-review-case-top">
                      <div>
                        <p class="qa-tc-review-case-id mb-0">{{ tc.tcLabel }}</p>
                        <h6 class="qa-tc-review-case-name mb-1">{{ tc.title || "Untitled" }}</h6>
                        <div class="qa-tc-review-type-row">
                          <span class="qa-tc-review-type-item">
                            <strong class="qa-tc-review-k">Type:</strong>
                            <span class="qa-tc-review-type-val" :class="reviewTypeToneClass(tc.testType)">{{ enumLabel(tc.testType) }}</span>
                          </span>
                          <span class="qa-tc-review-type-item">
                            <strong class="qa-tc-review-k">Priority:</strong>
                            <span class="qa-tc-review-priority-val" :class="reviewPriorityToneClass(tc.priority)">{{ enumLabel(tc.priority) }}</span>
                          </span>
                        </div>
                        <div class="qa-tc-review-hierarchy">
                          <div><strong class="qa-tc-review-k">Function:</strong> {{ tc.function || "—" }}</div>
                          <div><strong class="qa-tc-review-k">Scenario:</strong> {{ tc.scenario || "—" }}</div>
                          <div><strong class="qa-tc-review-k">Sub-Scenario:</strong> {{ tc.subScenario || "—" }}</div>
                        </div>
                      </div>
                      <div class="qa-tr-view-result-head">
                        <strong class="qa-tc-review-k">Result:</strong>
                        <span
                          class="qa-tr-view-result-chip"
                          :class="[
                            viewResultChipClass(tc.runStatus),
                            tc.runStatus === 'Pass with Condition' ? 'is-pass-condition' : '',
                          ]"
                        >{{ displayRunResult(tc.runStatus) }}</span>
                      </div>
                    </div>
                    <div class="qa-tc-review-fields">
                      <div>
                        <p class="qa-tc-review-label">Pre-Condition</p>
                        <div class="qa-tc-review-value">
                          <BulletText :text="tc.preconditions" list-class="mb-0" fallback="—" />
                        </div>
                      </div>
                      <div>
                        <p class="qa-tc-review-label">Test Data</p>
                        <div class="qa-tc-review-value">
                          <BulletText :text="tc.testData" list-class="mb-0" fallback="—" />
                        </div>
                      </div>
                    </div>
                    <table class="qa-tc-review-steps">
                      <colgroup>
                        <col class="qa-tc-review-col-no" />
                        <col class="qa-tc-review-col-module" />
                        <col class="qa-tc-review-col-step" />
                        <col class="qa-tc-review-col-expected" />
                        <col class="qa-tc-review-col-actual" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>Step</th>
                          <th>Module</th>
                          <th>Test Step</th>
                          <th>Expected Result</th>
                          <th>Actual Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!(tc.steps || []).length">
                          <td class="text-secondary">—</td>
                          <td class="text-secondary">—</td>
                          <td class="text-secondary">—</td>
                          <td class="text-secondary">—</td>
                          <td>
                            <BulletText :text="viewOverviewActual(tc)" list-class="mb-0" fallback="—" />
                            <div v-if="viewActualMediaPreview(tc).length" class="qa-tr-view-media mt-1">
                              <button
                                v-for="item in viewActualMediaPreview(tc)"
                                :key="item.index"
                                type="button"
                                class="qa-tr-view-media-thumb"
                                @click="openViewMedia(tc, item.index)"
                              >
                                <video v-if="isVideoMediaUrl(item.src)" :src="item.src" muted preload="metadata" playsinline />
                                <img v-else :src="item.src" alt="" referrerpolicy="no-referrer" />
                                <span v-if="isVideoMediaUrl(item.src) && !item.extra" class="qa-tr-view-media-play">
                                  <i class="fas fa-play"></i>
                                </span>
                                <span v-if="item.extra" class="qa-tr-view-media-more">+{{ item.extra }}</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                        <tr v-for="(step, idx) in tc.steps" :key="`${tc.key}-std-${idx}`">
                          <td>{{ step.stepNo || idx + 1 }}</td>
                          <td><BulletText :text="step.platform" list-class="mb-0" fallback="—" /></td>
                          <td><BulletText :text="step.stepDetail" list-class="mb-0" fallback="—" /></td>
                          <td><BulletText :text="step.expectedResult" list-class="mb-0" fallback="—" /></td>
                          <td v-if="viewActualIsByStep(tc) || idx === 0" :rowspan="viewActualIsByStep(tc) ? 1 : tc.steps.length">
                            <BulletText :text="viewActualText(tc, idx)" list-class="mb-0" fallback="—" />
                            <div v-if="viewStepActualMediaPreview(tc, idx).length" class="qa-tr-view-media mt-1">
                              <button
                                v-for="item in viewStepActualMediaPreview(tc, idx)"
                                :key="item.index"
                                type="button"
                                class="qa-tr-view-media-thumb"
                                @click="openViewStepMedia(tc, idx, item.index)"
                              >
                                <video v-if="isVideoMediaUrl(item.src)" :src="item.src" muted preload="metadata" playsinline />
                                <img v-else :src="item.src" alt="" referrerpolicy="no-referrer" />
                                <span v-if="isVideoMediaUrl(item.src) && !item.extra" class="qa-tr-view-media-play">
                                  <i class="fas fa-play"></i>
                                </span>
                                <span v-if="item.extra" class="qa-tr-view-media-more">+{{ item.extra }}</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </article>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="lightboxSrc" class="lightbox-overlay">
        <button type="button" class="lightbox-close" title="Close" @click="closeLightbox">
          <i class="fas fa-times"></i>
        </button>
        <button
          v-if="lightboxItems.length > 1"
          type="button"
          class="lightbox-nav lightbox-prev"
          title="Previous"
          @click.stop="lightboxPrev"
        >
          <i class="fas fa-chevron-left"></i>
        </button>
        <video v-if="lightboxIsVideo" :src="lightboxSrc" class="lightbox-img" controls autoplay />
        <img v-else :src="lightboxSrc" class="lightbox-img" />
        <button
          v-if="lightboxItems.length > 1"
          type="button"
          class="lightbox-nav lightbox-next"
          title="Next"
          @click.stop="lightboxNext"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
        <span v-if="lightboxItems.length > 1" class="lightbox-count">{{ lightboxIndex + 1 }} / {{ lightboxItems.length }}</span>
      </div>
    </Teleport>
  </div>
</template>

<script>
import SoftProgress from "@/components/SoftProgress";
import BulletText from "@/components/BulletText.vue";
import { mapState } from "vuex";
import { API_BASE_URL, fetchWithTimeout } from "@/config/apiBase";
import { testRunsApi, testRunDetailsApi, usersApi, realTestCasesApi, testPlansApi, projectsApi } from "@/services/backendApi";
import { normalizeTeamCode, TEAM_OPTIONS } from "@/utils/userTeams";
import { isPlanHistoryStatus, subProjectStatusLabel } from "@/utils/subProjectStatus";

// Thai public holidays 2025–2026
const THAI_HOLIDAYS = new Set([
  "2025-01-01","2025-02-12","2025-04-06","2025-04-07","2025-04-14","2025-04-15","2025-04-16",
  "2025-05-01","2025-05-12","2025-06-03","2025-07-11","2025-07-28","2025-08-12",
  "2025-10-13","2025-10-23","2025-12-05","2025-12-10","2025-12-31",
  "2026-01-01","2026-03-04","2026-04-06","2026-04-13","2026-04-14","2026-04-15",
  "2026-05-01","2026-05-22","2026-06-03","2026-07-08","2026-07-28","2026-08-12",
  "2026-10-13","2026-10-23","2026-12-05","2026-12-10","2026-12-31",
]);

function isByStepMode(mode) {
  const m = String(mode || "").trim().toLowerCase();
  return m === "step" || m === "bystep";
}

function isVideoMediaUrl(url) {
  if (!url || typeof url !== "string") return false;
  if (url.startsWith("data:video")) return true;
  return /\.(mp4|webm|mov)(\?|$)/i.test(url.toLowerCase());
}

function countWorkingDays(start, end) {
  if (!start || !end) return 0;
  let count = 0;
  const d = new Date(start);
  const e = new Date(end);
  if (d > e) return 0;
  while (d <= e) {
    const day = d.getDay();
    const ds = d.toISOString().substring(0, 10);
    if (day !== 0 && day !== 6 && !THAI_HOLIDAYS.has(ds)) count++;
    d.setDate(d.getDate() + 1);
  }
  return count;
}

function formatDateDisplay(val) {
  if (!val) return "—";
  const s = String(val).substring(0, 10);
  const [y, m, d] = s.split("-");
  return `${d}-${m}-${y}`;
}

const STAGE_OPTIONS = [
  { label: "Regression", value: "Regression" },
  { label: "Execute", value: "Execute" },
  { label: "PVT", value: "PVT" },
];

const ENVIRONMENT_OPTIONS = [
  { label: "DEV", value: "DEV" },
  { label: "SIT", value: "SIT" },
  { label: "STG", value: "STG" },
  { label: "PRD", value: "PRD" },
];

const RUN_STATUS_OPTIONS = [
  { label: "Not Started", value: "Not Started" },
  { label: "In Progress", value: "In Progress" },
  { label: "Done", value: "Done" },
  { label: "Done without Deploy", value: "Done without Deploy" },
  { label: "Deployed", value: "Deployed" },
  { label: "Failed", value: "Failed" },
];

export default {
  name: "test-run-tab",
  components: { SoftProgress, BulletText },
  props: {
    availablePlans: { type: Array, default: () => [] },
    projectId: { type: String, default: "proj_001" },
    projectPlanId: { type: [String, Number], default: "" },
    standalone: { type: Boolean, default: false },
    teamFilter: { type: String, default: "" },
  },
  data() {
    return {
      search: "",
      historyTeamFilter: "",
      historyQaFilter: "",
      showCreateModal: false,
      showAssigneeDropdown: false,
      showConfigDropdown: false,
      editMode: false,
      editingRunId: null,
      loading: false,
      users: [],
      allTestCases: [],
      configOptions: ["Mobile iOS", "Mobile Android", "Web browser", "Web Application", "API"],
      form: this.blankForm(),
      runs: [],
      jiraOptions: [],
      showJiraDropdown: false,
      savingRun: false,
      savingRunLabel: "Saving...",
      saveRunError: null,
      originalDetailByTcId: {},
      copySourceTeam: "",
      copyMode: false,
      copySourceRunId: "",
      plans: [],
      catalogProjects: [],
      catalogFunctions: [],
      catalogFolders: [],
      catalogLoading: false,
      catalog: { projectId: "", functionId: "", folderId: "" },
      caseSources: [],
      formFieldErrors: {},
      selectedRunId: null,
      showRunView: false,
      runViewMode: "standard",
      runViewLoading: false,
      runViewError: "",
      runViewCases: [],
      runViewFilters: { search: "", testType: "", priority: "", scenario: "", subScenario: "", status: "" },
      viewingRun: null,
      collapsedRunGroups: [],
      collapsedRunPlans: [],
      lightboxSrc: null,
      lightboxIsVideo: false,
      lightboxItems: [],
      lightboxIndex: 0,
    };
  },
  watch: {
    "form.name"() {
      this.clearFieldError("name");
    },
    "form.stage"() {
      this.clearFieldError("stage");
    },
    caseSources: {
      handler() {
        if (this.caseSources.length) this.clearFieldError("casesSource");
      },
    },
    "summaryDisplay.cases"(count) {
      if (Number(count) > 0) this.clearFieldError("cases");
    },
    showRunView() {
      this.syncRunViewStickyHeaders();
    },
    runViewMode() {
      this.syncRunViewStickyHeaders();
    },
    runViewFilteredCases() {
      this.syncRunViewStickyHeaders();
    },
    teamFilter() {
      this.historyTeamFilter = "";
      this.historyQaFilter = "";
    },
  },
  async created() {
    await Promise.all([this.loadRuns(), this.loadUsers(), this.loadJiraOptions(), this.loadPlans(), this.loadCatalogProjects()]);
    document.addEventListener("click", this.closeDropdowns);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeDropdowns);
    this.unbindRunViewStickyHeaders();
  },
  computed: {
    ...mapState(["user"]),
    isLeadRole() {
      const roles = this.user?.roles || [];
      if (this.user?.roleName && !roles.length) {
        const n = String(this.user.roleName).trim().toLowerCase();
        return n === "lead" || n.includes("lead");
      }
      return roles.some((r) => {
        const n = String(r.roleName || "").trim().toLowerCase();
        return n === "lead" || n.includes("lead");
      });
    },
    resolvedPlanId() {
      return String(
        this.projectPlanId
        || this.form?.projectPlanId
        || this.$route.params.id
        || "",
      ).trim();
    },
    planSelectOptions() {
      const teamCode = this.activeTeamCode;
      const opts = (this.plans || [])
        .filter((p) => {
          if (!this.standalone) return true;
          if (isPlanHistoryStatus(p.testPlanStatus || p.status)) return false;
          if (!teamCode) return true;
          return this.teamCodeOf(p.teamTrimmed || p.team) === teamCode;
        })
        .map((p) => {
          const name = [p.subProjectName || p.testPlanName, p.projectName].filter(Boolean).join(" — ");
          const rawStatus = p.testPlanStatus || p.status;
          const status = rawStatus ? subProjectStatusLabel(rawStatus) : "";
          return {
            value: String(p.projectPlanId),
            label: status && status !== "—" ? `${name} (${status})` : name,
          };
        });
      if (this.copyMode && this.form.projectPlanId) {
        const id = String(this.form.projectPlanId);
        if (!opts.some((o) => String(o.value) === id)) {
          const run = (this.runs || []).find((r) => String(r.id) === String(this.copySourceRunId));
          const label = [run?.testPlanName || run?.subProjectName, run?.projectName].filter(Boolean).join(" — ")
            || `Plan ${id}`;
          opts.unshift({ value: id, label });
        }
      }
      return opts;
    },
    catalogProjectLocked() {
      return this.caseSources.length > 0;
    },
    lockedCatalogProjectId() {
      return String(this.caseSources[0]?.projectId || "");
    },
    runStatusOptions() {
      return RUN_STATUS_OPTIONS;
    },
    selectedRun() {
      const id = this.selectedRunId;
      if (id == null || id === "") return null;
      return (this.filteredRuns || []).find((r) => String(r.id) === String(id)) || null;
    },
    copyRunSelectOptions() {
      return (this.filteredRuns || []).map((r) => ({
        value: String(r.id),
        label: r.name || `Test Run ${r.id}`,
      }));
    },
    runViewFilteredCases() {
      const f = this.runViewFilters || {};
      const scenario = String(f.scenario || "");
      const subScenario = String(f.subScenario || "");
      const status = String(f.status || "").trim();
      const testType = String(f.testType || "").trim().toLowerCase();
      const priority = String(f.priority || "").trim().toLowerCase();
      const q = String(f.search || "").trim().toLowerCase();
      return (this.runViewCases || []).filter((tc) => {
        if (scenario && String(tc.scenario || "") !== scenario) return false;
        if (subScenario && String(tc.subScenario || "") !== subScenario) return false;
        if (status) {
          const raw = String(tc.runStatus || "");
          const label = this.displayRunResult(raw);
          if (raw !== status && label !== status) return false;
        }
        if (testType && String(tc.testType || tc.typeOfTestCase || "").trim().toLowerCase() !== testType) return false;
        if (priority && String(tc.priority || "").trim().toLowerCase() !== priority) return false;
        if (!q) return true;
        const hay = [
          tc.tcLabel,
          tc.title,
          tc.scenario,
          tc.subScenario,
          tc.testType,
          tc.typeOfTestCase,
          tc.priority,
          tc.runStatus,
          this.displayRunResult(tc.runStatus),
        ].join(" ").toLowerCase();
        return hay.includes(q);
      });
    },
    runViewTypeFilterOptions() {
      return [
        { label: "All Type", value: "" },
        { label: "Positive", value: "Positive" },
        { label: "Negative", value: "Negative" },
      ];
    },
    runViewPriorityFilterOptions() {
      return [
        { label: "All Priority", value: "" },
        { label: "High", value: "high" },
        { label: "Medium", value: "medium" },
        { label: "Low", value: "low" },
      ];
    },
    runViewScenarioFilterOptions() {
      const values = [...new Set(
        (this.runViewCases || []).map((tc) => String(tc.scenario || "").trim()).filter(Boolean)
      )].sort((a, b) => a.localeCompare(b));
      return [{ label: "All Scenario", value: "" }, ...values.map((value) => ({ label: value, value }))];
    },
    runViewSubScenarioFilterOptions() {
      const scenario = String(this.runViewFilters.scenario || "");
      const values = [...new Set(
        (this.runViewCases || [])
          .filter((tc) => !scenario || String(tc.scenario || "") === scenario)
          .map((tc) => String(tc.subScenario || "").trim())
          .filter(Boolean)
      )].sort((a, b) => a.localeCompare(b));
      return [{ label: "All Sub-Scenario", value: "" }, ...values.map((value) => ({ label: value, value }))];
    },
    runViewStatusFilterOptions() {
      const order = ["No Run", "In progress", "In Progress", "Pass", "Pass with Condition", "Fail", "Failed", "Blocked"];
      const set = new Set((this.runViewCases || []).map((tc) => String(tc.runStatus || "").trim()).filter(Boolean));
      const values = [...set].sort((a, b) => {
        const ai = order.indexOf(a);
        const bi = order.indexOf(b);
        if (ai === -1 && bi === -1) return a.localeCompare(b);
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });
      return [
        { label: "All Status", value: "" },
        ...values.map((value) => ({ label: this.displayRunResult(value), value })),
      ];
    },
    runViewGroupedCases() {
      const groups = [];
      const byScenario = {};
      for (const tc of this.runViewFilteredCases) {
        const scenario = tc.scenario || "(No Scenario)";
        if (!byScenario[scenario]) {
          byScenario[scenario] = { scenario, subGroups: [], _sub: {} };
          groups.push(byScenario[scenario]);
        }
        const sub = tc.subScenario || "(No Sub-Scenario)";
        if (!byScenario[scenario]._sub[sub]) {
          byScenario[scenario]._sub[sub] = { subScenario: sub, cases: [] };
          byScenario[scenario].subGroups.push(byScenario[scenario]._sub[sub]);
        }
        byScenario[scenario]._sub[sub].cases.push(tc);
      }
      return groups;
    },
    isHistoryTeamTab() {
      return this.standalone && String(this.teamFilter || "").trim().toLowerCase() === "history";
    },
    activeTeamCode() {
      if (!this.standalone || this.isHistoryTeamTab) return "";
      const key = String(this.teamFilter || "").trim().toLowerCase();
      if (key === "unassign") return "c";
      return normalizeTeamCode(key);
    },
    emptyRunsMessage() {
      if (this.isHistoryTeamTab) return "No archived test runs yet.";
      return 'No test runs yet. Click "New Test Run" to create one.';
    },
    historyTeamOptions() {
      return [
        { label: "All Team", value: "" },
        ...TEAM_OPTIONS.filter((opt) => opt.value !== "all").map((opt) => ({
          label: opt.label,
          value: opt.value,
        })),
      ];
    },
    historyQaOptions() {
      const opts = (this.users || []).map((u) => ({
        label: this.formatUserDisplayName(u),
        value: String(u.userId),
      })).filter((o) => o.label && o.label !== "—");
      opts.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
      return [{ label: "All QA", value: "" }, ...opts];
    },
    filteredRuns() {
      let list = this.runs || [];
      if (this.standalone && this.teamFilter) {
        if (this.isHistoryTeamTab) {
          list = list.filter((r) => this.isHistoryRun(r));
          if (this.historyTeamFilter) {
            const code = this.teamCodeOf(this.historyTeamFilter);
            list = list.filter((r) => this.teamCodeOf(r.teamTrimmed || r.team) === code);
          }
          if (this.historyQaFilter) {
            const uid = Number(this.historyQaFilter);
            list = list.filter((r) => {
              const ids = Array.isArray(r.assignRun) ? r.assignRun.map(Number) : [];
              return ids.includes(uid);
            });
          }
        } else {
          const code = this.activeTeamCode;
          list = list.filter((r) => {
            if (this.isHistoryRun(r)) return false;
            return this.teamCodeOf(r.teamTrimmed || r.team) === code;
          });
        }
      }
      if (!this.search) return list;
      const q = this.search.toLowerCase();
      return list.filter((r) => {
        const hay = [
          r.name,
          r.stage,
          r.environment,
          r.subProjectName,
          r.testPlanName,
        ].join(" ").toLowerCase();
        return hay.includes(q);
      });
    },
    groupedFilteredRuns() {
      const subs = new Map();
      (this.filteredRuns || []).forEach((run) => {
        const subName = String(run.subProjectName || "").trim();
        const planId = run.testPlanId || run.projectPlanId || "";
        const planName = String(
          run.testPlanName || run.planTaskName || this.planNameById(planId) || ""
        ).trim();
        const subKey = subName ? `sub:${subName}` : "sub:none";
        const planKey = planId ? `plan:${planId}` : "unlinked";
        if (!subs.has(subKey)) {
          subs.set(subKey, {
            key: subKey,
            label: subName || "No Sub Project",
            sort: subName ? subName.toLowerCase() : "\uffff",
            plans: new Map(),
          });
        }
        const sub = subs.get(subKey);
        if (!sub.plans.has(planKey)) {
          sub.plans.set(planKey, {
            key: planKey,
            label: planId ? (planName || "Plan") : "No Plan",
            sort: planId ? (planName || "plan").toLowerCase() : "\uffff",
            runs: [],
          });
        }
        sub.plans.get(planKey).runs.push(run);
      });
      return [...subs.values()]
        .map((sub) => ({
          ...sub,
          count: [...sub.plans.values()].reduce((n, p) => n + p.runs.length, 0),
          plans: [...sub.plans.values()].sort((a, b) => a.sort.localeCompare(b.sort)),
        }))
        .sort((a, b) => a.sort.localeCompare(b.sort));
    },
    uniqueScenarios() {
      const seen = {};
      return this.modalTestCases.reduce((acc, tc) => {
        if (tc.scenarioId && !seen[tc.scenarioId]) {
          seen[tc.scenarioId] = true;
          acc.push({ id: tc.scenarioId, name: tc.scenario || tc.scenarioName });
        }
        return acc;
      }, []);
    },
    stageOptions() {
      return STAGE_OPTIONS;
    },
    environmentOptions() {
      return ENVIRONMENT_OPTIONS;
    },
    scenarioSelectOptions() {
      return this.uniqueScenarios.map((s) => ({ label: s.name, value: s.id }));
    },
    modalTestCases() {
      return this.allTestCases;
    },
    scenarioRowsForCategory() {
      return this.form.scenarioRows;
    },
    tcRowsForCategory() {
      return this.form.tcRows;
    },
    canAddAllScenarios() {
      if (this.uniqueScenarios.length === 0) return false;
      return this.uniqueScenarios.some((s) => this.hasAvailableSubScenariosForScenario(s.id));
    },
    summaryDisplay() {
      const startDate = this.form.runStartDate || this.form.fallbackStartDate || "";
      const dueDate = this.form.runDueDate || this.form.fallbackDueDate || "";

      let cases = 0;
      this.form.scenarioRows.forEach((r) => { cases += this.caseCountForScenarioRow(r); });
      this.form.tcRows.forEach((r) => { if (r.testCaseId) cases += 1; });
      if (cases === 0 && this.form.fallbackTotal) cases = this.form.fallbackTotal;

      return {
        startDate: formatDateDisplay(startDate),
        dueDate: formatDateDisplay(dueDate),
        days: countWorkingDays(startDate, dueDate),
        cases,
        rawStart: startDate,
        rawDue: dueDate,
      };
    },
    catalogProjectOptions() {
      return (this.catalogProjects || []).map((p) => ({
        value: String(p.projectId),
        label: p.projectName || `Project ${p.projectId}`,
      }));
    },
    catalogFunctionOptions() {
      const byId = new Map();
      for (const f of this.catalogFunctions || []) {
        const id = String(f.functionId ?? "");
        if (id && byId.has(id)) continue;
        byId.set(id || `row-${byId.size}`, f);
      }
      const grouped = new Map();
      for (const f of byId.values()) {
        const key = String(f.functionName || "").trim().toLowerCase() || "(blank)";
        const prev = grouped.get(key);
        if (!prev) {
          grouped.set(key, f);
          continue;
        }
        if (/^\d+$/.test(String(f.functionId || "")) && !/^\d+$/.test(String(prev.functionId || ""))) {
          grouped.set(key, f);
        }
      }
      return [...grouped.values()]
        .sort((a, b) => String(a.functionName || "").localeCompare(String(b.functionName || ""), undefined, { sensitivity: "base" }))
        .map((f) => ({
          value: String(f.functionId),
          label: f.functionName || `Group ${f.functionId}`,
        }));
    },
    catalogFolderOptions() {
      return (this.catalogFolders || []).map((f) => ({
        value: String(f.folderId || "uncategorized"),
        label: f.folderName || "Uncategorized",
      }));
    },
  },
  methods: {
    formatDateDisplay,
    planNameById(planId) {
      if (!planId) return "";
      const id = String(planId);
      const p = (this.plans || []).find(
        (x) => String(x.projectPlanId || "") === id || String(x.testPlanId || "") === id
      );
      return String(p?.testPlanName || "").trim();
    },
    isRunOverdue(run) {
      const due = String(run?.dueDate || "").substring(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(due)) return false;
      const status = String(run?.status || "").trim();
      if (["Done", "Done without Deploy", "Deployed"].includes(status)) return false;
      const now = new Date();
      const today = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, "0"),
        String(now.getDate()).padStart(2, "0"),
      ].join("-");
      return due < today;
    },
    teamCodeOf(raw) {
      const code = normalizeTeamCode(raw);
      if (code === "a" || code === "b" || code === "c") return code;
      return "c";
    },
    isHistoryRun(run) {
      return isPlanHistoryStatus(run?.planStatus) || isPlanHistoryStatus(run?.status);
    },
    async loadPlans() {
      if (!this.standalone) return;
      try {
        const rows = await testPlansApi.getAll();
        this.plans = Array.isArray(rows) ? rows : [];
      } catch (e) {
        console.error("loadPlans error:", e);
        this.plans = [];
      }
    },
    async onStandalonePlanChange(value) {
      this.form.projectPlanId = value ? String(value) : "";
      await this.loadJiraOptions();
    },
    async loadCatalogProjects() {
      try {
        const rows = await projectsApi.getAll();
        this.catalogProjects = Array.isArray(rows) ? rows : [];
      } catch (e) {
        console.error("loadCatalogProjects error:", e);
        this.catalogProjects = [];
      }
    },
    async onCatalogProjectChange(value) {
      if (this.catalogProjectLocked) {
        this.catalog.projectId = this.lockedCatalogProjectId;
        return;
      }
      this.catalog.projectId = value ? String(value) : "";
      this.catalog.functionId = "";
      this.catalog.folderId = "";
      this.catalogFolders = [];
      this.catalogFunctions = [];
      if (!this.catalog.projectId) return;
      try {
        const rows = await realTestCasesApi.listFunctions(this.catalog.projectId);
        this.catalogFunctions = Array.isArray(rows) ? rows : [];
      } catch (e) {
        console.error("onCatalogProjectChange error:", e);
        this.catalogFunctions = [];
      }
    },
    catalogFunctionById(functionId) {
      return (this.catalogFunctions || []).find((f) => String(f.functionId) === String(functionId || "")) || null;
    },
    async onCatalogFunctionChange(value) {
      this.catalog.functionId = value ? String(value) : "";
      this.catalog.folderId = "";
      this.catalogFolders = [];
      const selected = this.catalogFunctionById(this.catalog.functionId);
      const key = String(selected?.functionName || "").trim().toLowerCase();
      const targets = key
        ? (this.catalogFunctions || []).filter(
          (f) => String(f.functionName || "").trim().toLowerCase() === key,
        )
        : (selected ? [selected] : []);
      if (!targets.length) return;
      try {
        const lists = await Promise.all(targets.map((fn) => (
          fn.subProjectId
            ? realTestCasesApi.listFolders(fn.subProjectId, fn.functionId).catch(() => [])
            : Promise.resolve([])
        )));
        const seen = new Set();
        const folders = [];
        for (const rows of lists) {
          for (const row of (Array.isArray(rows) ? rows : [])) {
            const id = `${row.folderId}::${row.subProjectId || ""}`;
            if (seen.has(id)) continue;
            seen.add(id);
            folders.push(row);
          }
        }
        this.catalogFolders = folders;
      } catch (e) {
        console.error("onCatalogFunctionChange error:", e);
        this.catalogFolders = [];
      }
    },
    catalogSourceKey(src) {
      return [src.projectId, src.functionId || "all", src.folderId || "all"].join(":");
    },
    catalogSourceLabel(src) {
      const project = this.catalogProjects.find((p) => String(p.projectId) === String(src.projectId));
      const fn = this.catalogFunctionById(src.functionId);
      const folder = this.catalogFolders.find((f) => String(f.folderId || "uncategorized") === String(src.folderId));
      const parts = [
        src.projectName || project?.projectName || `Project ${src.projectId}`,
      ];
      if (src.functionId) parts.push(src.functionName || fn?.functionName || `Group ${src.functionId}`);
      else parts.push("All groups");
      if (src.folderId) parts.push(src.folderName || folder?.folderName || (src.folderId === "uncategorized" ? "Uncategorized" : `Folder ${src.folderId}`));
      else if (src.functionId) parts.push("All folders");
      return parts.join(" / ");
    },
    normalizeCatalogCase(tc) {
      return {
        ...tc,
        testCaseId: tc.testCaseId,
        scenarioId: tc.scenarioId,
        scenario: tc.scenario || tc.scenarioName || "",
        subScenarioId: tc.subScenarioId || "",
        subScenario: tc.subScenario || tc.subScenarioName || "",
        typeOfTestCase: tc.typeOfTestCase,
        title: tc.title || "",
        testCaseNumber: tc.testCaseNumber,
      };
    },
    mergeCatalogCases(rows) {
      const byId = {};
      for (const tc of this.allTestCases) byId[String(tc.testCaseId)] = tc;
      for (const raw of Array.isArray(rows) ? rows : []) {
        const tc = this.normalizeCatalogCase(raw);
        if (!tc.testCaseId) continue;
        byId[String(tc.testCaseId)] = { ...byId[String(tc.testCaseId)], ...tc };
      }
      this.allTestCases = Object.values(byId);
    },
    async addCatalogSource() {
      if (!this.catalog.projectId) return;
      if (this.catalogProjectLocked && String(this.catalog.projectId) !== this.lockedCatalogProjectId) {
        this.saveRunError = "Additional folders must stay in the same project. Change Group or Folder instead.";
        this.catalog.projectId = this.lockedCatalogProjectId;
        return;
      }
      const fn = this.catalogFunctionById(this.catalog.functionId);
      const src = {
        projectId: this.catalog.projectId,
        functionId: this.catalog.functionId || "",
        subProjectId: fn?.subProjectId || "",
        folderId: this.catalog.folderId || "",
        projectName: this.catalogProjects.find((p) => String(p.projectId) === this.catalog.projectId)?.projectName || "",
        functionName: fn?.functionName || "",
        folderName: this.catalogFolders.find((f) => String(f.folderId || "uncategorized") === this.catalog.folderId)?.folderName || "",
      };
      src.key = this.catalogSourceKey(src);
      src.label = this.catalogSourceLabel(src);
      this.catalogLoading = true;
      try {
        const rows = await realTestCasesApi.getByProject(src.projectId, {
          subProjectId: src.subProjectId || undefined,
          functionId: src.functionId || undefined,
          folderId: src.folderId || undefined,
        });
        this.mergeCatalogCases(rows);
        if (!this.caseSources.some((s) => s.key === src.key)) this.caseSources.push(src);
        this.catalog.folderId = "";
      } catch (e) {
        console.error("addCatalogSource error:", e);
        this.saveRunError = e.message || "Failed to load test cases.";
      } finally {
        this.catalogLoading = false;
      }
    },
    async reloadCasesFromSources() {
      this.allTestCases = [];
      if (!this.caseSources.length) return;
      this.catalogLoading = true;
      try {
        const batches = await Promise.all(this.caseSources.map((src) => (
          realTestCasesApi.getByProject(src.projectId, {
            subProjectId: src.subProjectId || undefined,
            functionId: src.functionId || undefined,
            folderId: src.folderId || undefined,
          }).catch(() => [])
        )));
        batches.forEach((rows) => this.mergeCatalogCases(rows));
      } finally {
        this.catalogLoading = false;
      }
    },
    async removeCatalogSource(key) {
      this.caseSources = this.caseSources.filter((s) => s.key !== key);
      await this.reloadCasesFromSources();
    },
    async loadJiraOptions() {
      try {
        const planId = this.resolvedPlanId;
        if (!planId) {
          this.jiraOptions = [];
          return;
        }
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/project-plans/${planId}`);
        const data = await res.json();
        if (data.projectLink) {
          const links = JSON.parse(data.projectLink);
          this.jiraOptions = Array.isArray(links) ? links : [];
        }
      } catch (e) { console.error("loadJiraOptions error:", e); }
    },
    toggleJiraUrl(j) {
      const idx = this.form.jiraUrls.findIndex(x => x.key === j.key);
      if (idx >= 0) this.form.jiraUrls.splice(idx, 1);
      else this.form.jiraUrls.push({ key: j.key, url: j.url });
    },
    removeJiraUrl(key) { this.form.jiraUrls = this.form.jiraUrls.filter(x => x.key !== key); },
    isJiraUrlSelected(key) { return this.form.jiraUrls.some(x => x.key === key); },
    blankForm() {
      return {
        name: "", stage: "", environment: "", description: "",
        userIds: [], configurations: [], version: "", state: "Not Started", jiraUrls: [],
        runStartDate: "",
        runDueDate: "",
        scenarioRows: [],
        tcRows: [],
        fallbackStartDate: "",
        fallbackDueDate: "",
        fallbackTotal: 0,
        projectPlanId: "",
      };
    },
    blankScenarioRow() {
      return {
        _id: Date.now() + Math.random(),
        scenarioId: "",
        subScenarioId: "",
      };
    },
    blankTcRow() {
      return {
        _id: Date.now() + Math.random(),
        scenarioId: "",
        subScenarioId: "",
        testCaseId: "",
      };
    },
    canDeleteRow(row) {
      if (this.copyMode) return false;
      if (!row?.isExisting) return true;
      return this.isLeadRole;
    },
    fullyUsedSubScenarioIdsForScenario(scenarioId, excludeRowId = null) {
      const used = new Set();
      const selected = this.getSelectedTestCaseIds(excludeRowId);
      const allSubs = this.subScenariosForScenario(scenarioId);
      for (const sub of allSubs) {
        const tcsInSub = this.testCasesForCategory(sub.id);
        if (tcsInSub.length === 0) continue;
        const allInRun = tcsInSub.every((tc) => selected.has(String(tc.testCaseId)));
        if (allInRun) used.add(String(sub.id));
      }
      return used;
    },
    hasAvailableSubScenariosForScenario(scenarioId) {
      const allSubs = this.subScenariosForScenario(scenarioId);
      if (allSubs.length === 0) return false;
      const used = this.fullyUsedSubScenarioIdsForScenario(scenarioId, null);
      return allSubs.some((sub) => !used.has(String(sub.id)));
    },
    subScenariosForScenarioRow(row) {
      const all = this.subScenariosForScenario(row.scenarioId);
      if (row.isExisting) {
        if (!row.subScenarioId) return [];
        const match = all.find((s) => String(s.id) === String(row.subScenarioId));
        if (match) return [match];
        const fromTc = this.allTestCases.find(
          (tc) => String(tc.subScenarioId) === String(row.subScenarioId)
        );
        return [{
          id: String(row.subScenarioId),
          name: fromTc?.subScenario || String(row.subScenarioId),
        }];
      }
      const used = this.fullyUsedSubScenarioIdsForScenario(row.scenarioId, row._id);
      return all.filter((s) => !used.has(String(s.id)));
    },
    subScenariosForTcRow(row) {
      const all = this.subScenariosForScenario(row.scenarioId);
      if (row.isExisting && row.subScenarioId) {
        const match = all.find((s) => String(s.id) === String(row.subScenarioId));
        if (match) return [match];
        const fromTc = this.allTestCases.find(
          (tc) => String(tc.subScenarioId) === String(row.subScenarioId)
        );
        return [{
          id: String(row.subScenarioId),
          name: fromTc?.subScenario || String(row.subScenarioId),
        }];
      }
      return all;
    },
    getSelectedTestCaseIds(excludeRowId = null) {
      const ids = new Set();
      this.form.scenarioRows.forEach((row) => {
        if (excludeRowId && row._id === excludeRowId) return;
        this.resolveTestCasesForScenarioRow(row).forEach((tc) => {
          ids.add(String(tc.testCaseId));
        });
      });
      this.form.tcRows.forEach((row) => {
        if (excludeRowId && row._id === excludeRowId) return;
        if (row.testCaseId) ids.add(String(row.testCaseId));
      });
      return ids;
    },
    testCasesForTcRow(row) {
      if (row.isExisting && row.testCaseId) {
        const tc = this.allTestCases.find((item) => String(item.testCaseId) === String(row.testCaseId));
        return tc ? [tc] : [];
      }
      const list = this.testCasesForSubScenario(row.subScenarioId);
      const selected = this.getSelectedTestCaseIds(row._id);
      return list.filter((tc) => !selected.has(String(tc.testCaseId)));
    },
    removeScenarioRow(id) {
      const row = this.form.scenarioRows.find((item) => item._id === id);
      if (!this.canDeleteRow(row)) return;
      const idx = this.form.scenarioRows.findIndex((row) => row._id === id);
      if (idx !== -1) this.form.scenarioRows.splice(idx, 1);
    },
    addAllScenariosForCategory() {
      for (const s of this.uniqueScenarios) {
        if (!this.hasAvailableSubScenariosForScenario(s.id)) continue;
        this.form.scenarioRows.push({
          _id: Date.now() + Math.random(),
          scenarioId: String(s.id),
          subScenarioId: "",
        });
      }
    },
    removeTcRow(id) {
      const row = this.form.tcRows.find((item) => item._id === id);
      if (!this.canDeleteRow(row)) return;
      const idx = this.form.tcRows.findIndex((row) => row._id === id);
      if (idx !== -1) this.form.tcRows.splice(idx, 1);
    },
    tcLabel(tc) {
      const num = tc.testCaseNumber || tc.testCaseId;
      const id = num ? `TC-${String(num).padStart(3, "0")}` : "";
      return id ? `${id}: ${tc.title || ""}` : (tc.title || "—");
    },
    subScenarioSelectOptionsForScenarioRow(row) {
      return this.subScenariosForScenarioRow(row).map((s) => ({
        label: s.name,
        value: s.id,
      }));
    },
    subScenarioSelectOptionsForTcRow(row) {
      return this.subScenariosForTcRow(row).map((s) => ({
        label: s.name,
        value: s.id,
      }));
    },
    testCaseSelectOptionsForRow(row) {
      return this.testCasesForTcRow(row).map((tc) => ({
        label: this.tcLabel(tc),
        value: tc.testCaseId,
      }));
    },
    onScenarioRowScenarioChange(row) {
      row.subScenarioId = "";
      row.lockedTestCaseIds = [];
    },
    onTcRowScenarioChange(row) {
      row.subScenarioId = "";
      row.testCaseId = "";
    },
    testCasesForCategory(subScenarioId) {
      if (!subScenarioId) return [];
      return this.allTestCases.filter(
        (tc) => String(tc.subScenarioId) === String(subScenarioId)
      );
    },
    subScenariosForScenario(scenarioId) {
      if (!scenarioId) return [];
      const seen = {};
      const subs = [];
      const addSub = (subScenarioId, subScenarioName) => {
        const id = String(subScenarioId);
        if (!id || seen[id]) return;
        seen[id] = true;
        subs.push({ id, name: subScenarioName || id });
      };
      this.allTestCases.forEach((tc) => {
        if (String(tc.scenarioId) !== String(scenarioId)) return;
        if (tc.subScenarioId) addSub(tc.subScenarioId, tc.subScenario);
      });
      this.form.tcRows.forEach((row) => {
        if (String(row.scenarioId) !== String(scenarioId)) return;
        if (!row.subScenarioId) return;
        const fromTc = this.allTestCases.find(
          (tc) => String(tc.subScenarioId) === String(row.subScenarioId)
        );
        addSub(row.subScenarioId, fromTc?.subScenario);
      });
      return subs;
    },
    testCasesForSubScenario(subScenarioId) {
      return this.testCasesForCategory(subScenarioId);
    },
    testCasesForScenarioRow(row) {
      const tcs = this.resolveTestCasesForScenarioRow(row);
      if (row.isExisting) return tcs;
      const selected = this.getSelectedTestCaseIds(row._id);
      return tcs.filter((tc) => !selected.has(String(tc.testCaseId)));
    },
    resolveTestCasesForScenarioRow(row) {
      if (Array.isArray(row.lockedTestCaseIds) && row.lockedTestCaseIds.length > 0) {
        return row.lockedTestCaseIds
          .map((id) => this.allTestCases.find((tc) => String(tc.testCaseId) === String(id)))
          .filter(Boolean);
      }
      if (row.subScenarioId) {
        return this.testCasesForCategory(row.subScenarioId);
      }
      if (row.scenarioId) {
        return this.allTestCases.filter(
          (tc) => String(tc.scenarioId) === String(row.scenarioId)
        );
      }
      return [];
    },
    caseCountForScenarioRow(row) {
      return this.testCasesForScenarioRow(row).length;
    },
    closeDropdowns(e) {
      if (this.$refs.assigneeDropdownRef && !this.$refs.assigneeDropdownRef.contains(e.target)) {
        this.showAssigneeDropdown = false;
      }
      if (this.$refs.configDropdownRef && !this.$refs.configDropdownRef.contains(e.target)) {
        this.showConfigDropdown = false;
      }
    },
    toggleUser(uid) {
      const idx = this.form.userIds.indexOf(uid);
      if (idx === -1) this.form.userIds.push(uid);
      else this.form.userIds.splice(idx, 1);
    },
    removeUser(uid) {
      this.form.userIds = this.form.userIds.filter((id) => id !== uid);
    },
    userNameById(uid) {
      const u = this.users.find((u) => Number(u.userId) === uid);
      return u ? this.formatUserDisplayName(u) : uid;
    },
    formatUserDisplayName(user) {
      const first = String(user?.firstName || "").trim();
      const nick = String(user?.nickname || "").trim();
      if (!first) return "—";
      return nick ? `${first} (${nick})` : first;
    },
    formatAssignedName(rawName) {
      const name = String(rawName || "").trim();
      if (!name) return "—";
      const nicknameMatch = name.match(/\(([^)]+)\)/);
      const firstName = name.replace(/\([^)]*\)/g, " ").trim().split(/\s+/)[0] || "";
      if (nicknameMatch && nicknameMatch[1]) {
        return `${firstName} (${nicknameMatch[1].trim()})`;
      }
      return firstName;
    },
    toggleConfig(c) {
      const idx = this.form.configurations.indexOf(c);
      if (idx === -1) this.form.configurations.push(c);
      else this.form.configurations.splice(idx, 1);
    },
    removeConfig(c) {
      this.form.configurations = this.form.configurations.filter((x) => x !== c);
    },
    resolveSaveStatus() {
      const status = String(this.form.state || "Not Started").trim();
      return status || "Not Started";
    },
    async openCreateModal() {
      this.editMode = false;
      this.editingRunId = null;
      this.copyMode = false;
      this.copySourceRunId = "";
      this.copySourceTeam = "";
      this.showAssigneeDropdown = false;
      this.showConfigDropdown = false;
      this.saveRunError = null;
      this.formFieldErrors = {};
      this.originalDetailByTcId = {};
      this.form = this.blankForm();
      this.allTestCases = [];
      this.caseSources = [];
      this.catalog = { projectId: "", functionId: "", folderId: "" };
      this.catalogFunctions = [];
      this.catalogFolders = [];
      this.showCreateModal = true;
    },
    mapCopiedConfigurations(raw) {
      if (!raw) return [];
      return String(raw)
        .split(",")
        .map((s) => {
          const v = s.trim();
          if (v === "Android") return "Mobile Android";
          if (v === "Web") return "Web browser";
          return v;
        })
        .filter(Boolean);
    },
    async hydrateCopyCatalog(run) {
      this.catalog = { projectId: "", functionId: "", folderId: "" };
      this.catalogFunctions = [];
      this.catalogFolders = [];
      this.caseSources = [];
      const projectName = String(run?.projectName || "").trim();
      const subName = String(run?.subProjectName || "").trim();
      const project = (this.catalogProjects || []).find(
        (p) => String(p.projectName || "").trim() === projectName
      );
      if (!project) {
        if (projectName || subName) {
          this.caseSources = [{
            key: "copy-source",
            projectId: "",
            functionId: "",
            folderId: "",
            projectName,
            functionName: subName,
            folderName: "",
            label: [projectName, subName].filter(Boolean).join(" / "),
          }];
        }
        return;
      }
      this.catalog.projectId = String(project.projectId);
      try {
        const rows = await realTestCasesApi.listFunctions(this.catalog.projectId);
        this.catalogFunctions = Array.isArray(rows) ? rows : [];
      } catch (e) {
        console.error("hydrateCopyCatalog project error:", e);
        this.catalogFunctions = [];
      }
      const fn = this.catalogFunctions.find(
        (f) => String(f.functionName || "").trim() === subName
      ) || this.catalogFunctions.find(
        (f) => String(f.subProjectName || "").trim() === subName
      ) || null;
      if (fn) {
        this.catalog.functionId = String(fn.functionId);
        try {
          const folders = await realTestCasesApi.listFolders(fn.subProjectId, fn.functionId);
          this.catalogFolders = Array.isArray(folders) ? folders : [];
        } catch (e) {
          console.error("hydrateCopyCatalog folder error:", e);
          this.catalogFolders = [];
        }
      }
      const src = {
        projectId: this.catalog.projectId,
        functionId: this.catalog.functionId || "",
        subProjectId: fn?.subProjectId || "",
        folderId: "",
        projectName: project.projectName || projectName,
        functionName: fn?.functionName || "",
        folderName: "",
      };
      src.key = this.catalogSourceKey(src);
      src.label = this.catalogSourceLabel(src);
      this.caseSources = [src];
    },
    async applyCopySource(run) {
      if (!run) return;
      const keptName = this.form?.name || "";
      this.copySourceRunId = String(run.id);
      this.copySourceTeam = this.teamCodeOf(run.teamTrimmed || run.team);
      this.showAssigneeDropdown = false;
      this.showConfigDropdown = false;
      this.saveRunError = null;
      this.originalDetailByTcId = {};
      const userIds = Array.isArray(run.assignRun)
        ? run.assignRun.map(Number)
        : (run.assignRun ? [Number(run.assignRun)] : []);
      this.form = {
        name: keptName,
        stage: run.stage || "",
        environment: run.environment || "",
        description: run.description || "",
        userIds,
        configurations: this.mapCopiedConfigurations(run.configurations),
        version: run.version || "",
        state: "Not Started",
        jiraUrls: [],
        runStartDate: run.startDate ? String(run.startDate).substring(0, 10) : "",
        runDueDate: run.dueDate ? String(run.dueDate).substring(0, 10) : "",
        scenarioRows: [],
        tcRows: [],
        fallbackStartDate: run.startDate || "",
        fallbackDueDate: run.dueDate || "",
        fallbackTotal: run.total || 0,
        projectPlanId: String(run.testPlanId || this.resolvedPlanId || ""),
      };
      this.allTestCases = [];
      await this.hydrateCopyCatalog(run);
      await this.reconstructRowsFromDetails(run.id, { asCopy: true });
    },
    async onCopySourceChange(value) {
      const id = value ? String(value) : "";
      this.copySourceRunId = id;
      this.clearFieldError("copySource");
      if (!id) {
        const keptName = this.form?.name || "";
        this.form = { ...this.blankForm(), name: keptName };
        this.allTestCases = [];
        this.caseSources = [];
        this.catalog = { projectId: "", functionId: "", folderId: "" };
        this.catalogFunctions = [];
        this.catalogFolders = [];
        this.copySourceTeam = "";
        return;
      }
      const run = (this.runs || []).find((r) => String(r.id) === id);
      if (run) await this.applyCopySource(run);
    },
    async openCopyModal(run) {
      this.editMode = false;
      this.editingRunId = null;
      this.copyMode = true;
      this.copySourceRunId = run?.id ? String(run.id) : "";
      this.copySourceTeam = "";
      this.showAssigneeDropdown = false;
      this.showConfigDropdown = false;
      this.saveRunError = null;
      this.formFieldErrors = {};
      this.originalDetailByTcId = {};
      this.form = this.blankForm();
      this.allTestCases = [];
      this.caseSources = [];
      this.catalog = { projectId: "", functionId: "", folderId: "" };
      this.catalogFunctions = [];
      this.catalogFolders = [];
      this.showCreateModal = true;
      if (run) await this.applyCopySource(run);
    },
    async openEditModal(run) {
      this.editMode = true;
      this.editingRunId = run.id;
      this.copyMode = false;
      this.copySourceRunId = "";
      this.copySourceTeam = "";
      this.showAssigneeDropdown = false;
      this.showConfigDropdown = false;
      this.saveRunError = null;
      this.formFieldErrors = {};
      this.originalDetailByTcId = {};
      const userIds = Array.isArray(run.assignRun) ? run.assignRun.map(Number) : (run.assignRun ? [Number(run.assignRun)] : []);
      const configurations = run.configurations
        ? run.configurations.split(", ").map((s) => {
            const v = s.trim();
            if (v === "Android") return "Mobile Android";
            if (v === "Web") return "Web browser";
            return v;
          }).filter(Boolean)
        : [];
      this.form = {
        name: run.name || "",
        stage: run.stage || "",
        environment: run.environment || "",
        description: run.description || "",
        userIds,
        configurations,
        version: run.version || "",
        state: run.status || "Not Started",
        jiraUrls: (() => { try { const v = run.jiraLink || run.jiraUrl; if (!v) return []; if (typeof v === 'string' && v.startsWith('[')) return JSON.parse(v); if (Array.isArray(v)) return v; return [{ key: v, url: v }]; } catch(_) { return []; } })(),
        runStartDate: run.startDate ? String(run.startDate).substring(0, 10) : "",
        runDueDate: run.dueDate ? String(run.dueDate).substring(0, 10) : "",
        scenarioRows: [],
        tcRows: [],
        fallbackStartDate: run.startDate || "",
        fallbackDueDate: run.dueDate || "",
        fallbackTotal: run.total || 0,
        projectPlanId: String(run.testPlanId || this.resolvedPlanId || ""),
      };
      this.allTestCases = [];
      this.caseSources = [];
      this.catalog = { projectId: "", functionId: "", folderId: "" };
      this.showCreateModal = true;
      await this.reconstructRowsFromDetails(run.id);
    },
    async reconstructRowsFromDetails(runId, { asCopy = false } = {}) {
      try {
        const details = await testRunDetailsApi.getByRun(runId);
        if (!details || details.length === 0) return;

        const seenTc = {};
        details.forEach((d) => {
          if (d.testCaseId && !seenTc[d.testCaseId]) seenTc[d.testCaseId] = d;
        });
        const uniqueDetails = Object.values(seenTc);

        const scenarioMap = {};
        const tcRows = [];
        const originalDetailByTcId = {};

        uniqueDetails.forEach((d) => {
          let tc = this.allTestCases.find((t) => String(t.testCaseId) === String(d.testCaseId));
          if (!tc && d.testCaseId) {
            tc = this.normalizeCatalogCase({
              testCaseId: d.testCaseId,
              scenarioId: d.scenarioId,
              scenario: d.scenario,
              subScenarioId: d.subScenarioId,
              subScenario: d.subScenario,
              typeOfTestCase: d.typeOfTestCase,
              title: d.title,
              testCaseNumber: d.testCaseNumber,
            });
            this.allTestCases.push(tc);
          }
          if (!tc) return;
          const sectionAt = String(d.sectionAt || "").trim().toLowerCase();
          const startDate = d.startDate ? String(d.startDate).substring(0, 10) : "";
          const dueDate = d.dueDate ? String(d.dueDate).substring(0, 10) : "";
          const sectionKey = sectionAt === "scenario" ? "scenario" : "select";

          if (!asCopy) {
            originalDetailByTcId[String(tc.testCaseId)] = {
              testCaseId: tc.testCaseId,
              startDate: startDate || null,
              dueDate: dueDate || null,
              sectionAt: sectionKey,
            };
          }

          if (sectionAt === "scenario") {
            const key = `${tc.subScenarioId || ""}|${tc.scenarioId}`;
            if (!scenarioMap[key]) {
              scenarioMap[key] = {
                _id: Date.now() + Math.random(),
                scenarioId: String(tc.scenarioId),
                subScenarioId: tc.subScenarioId ? String(tc.subScenarioId) : "",
                lockedTestCaseIds: [],
                isExisting: true,
              };
            }
            scenarioMap[key].lockedTestCaseIds.push(String(tc.testCaseId));
          } else {
            tcRows.push({
              _id: Date.now() + Math.random(),
              scenarioId: String(tc.scenarioId),
              subScenarioId: tc.subScenarioId ? String(tc.subScenarioId) : "",
              testCaseId: String(tc.testCaseId),
              isExisting: true,
            });
          }
        });

        this.originalDetailByTcId = asCopy ? {} : originalDetailByTcId;

        if (!asCopy && !this.form.runStartDate) {
          const starts = uniqueDetails
            .map((d) => (d.startDate ? String(d.startDate).substring(0, 10) : ""))
            .filter(Boolean)
            .sort();
          if (starts.length) this.form.runStartDate = starts[0];
        }
        if (!asCopy && !this.form.runDueDate) {
          const ends = uniqueDetails
            .map((d) => (d.dueDate ? String(d.dueDate).substring(0, 10) : ""))
            .filter(Boolean)
            .sort();
          if (ends.length) this.form.runDueDate = ends[ends.length - 1];
        }

        this.form.scenarioRows = Object.values(scenarioMap);
        this.form.tcRows = tcRows;
      } catch (e) {
        console.error("reconstructRowsFromDetails error:", e);
      }
    },
    async loadTestCasesForModal() {
      if (this.caseSources.length) {
        await this.reloadCasesFromSources();
        return;
      }
      this.allTestCases = [];
    },
    openDetailPage(run) {
      const planId = run.testPlanId || this.resolvedPlanId || "unplanned";
      this.$router.push({
        name: "Test Run Detail",
        params: { id: planId, runIndex: run.id },
        query: this.standalone ? { from: "qa" } : this.$route.query,
      });
    },
    async loadUsers() {
      try { this.users = await usersApi.getAll(); } catch (e) { console.error(e); }
    },
    async loadRuns() {
      this.loading = true;
      try {
        this.runs = await testRunsApi.getByProject(this.standalone ? undefined : this.resolvedPlanId);
      } catch (e) {
        console.error("loadRuns error:", e);
        this.runs = [];
      } finally {
        this.loading = false;
      }
    },
    applyDbFieldError(message) {
      const raw = String(message || "");
      const col = (raw.match(/null value in column "([^"]+)"/i) || [])[1] || "";
      const fieldByColumn = {
        test_run_name: "name",
        stage: "stage",
        environment: "environment",
        start_date: "runStartDate",
        due_date: "runDueDate",
      };
      const key = fieldByColumn[col];
      if (!key) return false;
      this.formFieldErrors = { ...this.formFieldErrors, [key]: "This field is required." };
      return true;
    },
    clearFieldError(key) {
      if (!this.formFieldErrors?.[key]) return;
      const next = { ...this.formFieldErrors };
      delete next[key];
      this.formFieldErrors = next;
    },
    collectRunItems() {
      const runStartDate = this.form.runStartDate || null;
      const runDueDate = this.form.runDueDate || null;
      const tcDateMap = {};
      this.form.scenarioRows.forEach((row) => {
        const tcs = this.testCasesForScenarioRow(row);
        tcs.forEach((tc) => {
          tcDateMap[tc.testCaseId] = { testCaseId: tc.testCaseId, startDate: runStartDate, dueDate: runDueDate, sectionAt: "scenario" };
        });
      });
      this.form.tcRows.forEach((row) => {
        if (!row.testCaseId) return;
        const selectedElsewhere = this.getSelectedTestCaseIds(row._id);
        if (!row.isExisting && selectedElsewhere.has(String(row.testCaseId))) return;
        tcDateMap[row.testCaseId] = { testCaseId: row.testCaseId, startDate: runStartDate, dueDate: runDueDate, sectionAt: "select" };
      });
      if (this.editMode && !this.isLeadRole) {
        Object.values(this.originalDetailByTcId).forEach((meta) => {
          const tcId = meta.testCaseId;
          if (!tcDateMap[tcId]) {
            tcDateMap[tcId] = {
              testCaseId: meta.testCaseId,
              startDate: runStartDate,
              dueDate: runDueDate,
              sectionAt: meta.sectionAt,
            };
          }
        });
      }
      return Object.values(tcDateMap);
    },
    validateRunForm(items) {
      const errors = {};
      if (this.copyMode && !this.copySourceRunId) {
        errors.copySource = "This field is required.";
      }
      if (!String(this.form.name || "").trim()) {
        errors.name = "This field is required.";
      }
      if (!String(this.form.stage || "").trim()) {
        errors.stage = "This field is required.";
      }
      if (!items.length) {
        if (this.copyMode) {
          errors.copySource = this.copySourceRunId
            ? "This test run has no test cases to copy."
            : "This field is required.";
        } else if (!this.caseSources.length && !this.editMode) {
          errors.casesSource = "Add a project or folder first.";
        } else {
          errors.cases = "Select at least one test case.";
        }
      }
      if (!this.standalone && !this.resolvedPlanId) {
        errors.plan = "This field is required.";
      }
      this.formFieldErrors = errors;
      return Object.keys(errors).length === 0;
    },
    async saveRun() {
      const items = this.collectRunItems();
      if (!this.validateRunForm(items)) return;
      const projectPlanId = this.resolvedPlanId || null;

      this.saveRunError = null;
      this.savingRun = true;
      this.savingRunLabel = "Saving...";
      const summary = this.summaryDisplay;
      if (items.length > 0) {
        this.savingRunLabel = `Saving ${items.length} test case${items.length === 1 ? "" : "s"}...`;
      }

      const payload = {
        projectId: projectPlanId,
        testPlanId: projectPlanId,
        team: this.activeTeamCode || this.copySourceTeam || null,
        owningTeam: this.activeTeamCode || this.copySourceTeam || null,
        name: this.form.name.trim(),
        stage: this.form.stage,
        environment: this.form.environment,
        description: this.form.description,
        startDate: summary.rawStart,
        dueDate: summary.rawDue,
        userIds: this.form.userIds,
        configurations: this.form.configurations,
        version: this.form.version,
        status: this.copyMode ? "Not Started" : this.resolveSaveStatus(),
        total: summary.cases,
        jiraUrl: this.editMode && this.form.jiraUrls.length ? JSON.stringify(this.form.jiraUrls) : null,
      };

      let runId = null;
      let headerSaved = false;
      try {
        if (this.editMode && this.editingRunId) {
          runId = this.editingRunId;
          await testRunsApi.update(runId, payload);
          headerSaved = true;
          await testRunDetailsApi.replace(runId, items);
        } else {
          const created = await testRunsApi.insert(payload);
          runId = created?.id ?? created?.testRunId ?? null;
          if (!runId) throw new Error("Test run was created but id was missing from the server response.");
          headerSaved = true;
          if (items.length > 0) {
            await testRunDetailsApi.replace(runId, items);
          }
        }
        await this.loadRuns();
        this.showCreateModal = false;
      } catch (e) {
        console.error("saveRun error:", e);
        if (headerSaved) {
          try { await this.loadRuns(); } catch (_) { /* ignore */ }
        }
        if (this.applyDbFieldError(e.message)) {
          this.saveRunError = null;
        } else if (headerSaved) {
          const isTimeout = /timed out/i.test(String(e.message || ""));
          this.saveRunError = isTimeout
            ? `Saving ${items.length} test cases took too long. The test run header was saved — open it from the list and edit to add test cases again, or try fewer scenarios.`
            : `${e.message || "Failed to save test cases."} The test run was saved — check the list or edit it to retry.`;
        } else {
          this.saveRunError = e.message || "Failed to save test run.";
        }
      } finally {
        this.savingRun = false;
      }
    },
    async deleteRun(run) {
      if (!confirm(`Delete "${run.name}"?`)) return;
      try {
        await testRunsApi.delete(run.id);
        await this.loadRuns();
      } catch (e) {
        console.error("deleteRun error:", e);
        this.runs = this.runs.filter((r) => r.id !== run.id);
      }
    },
    runProgress(run) {
      const passedStatuses = new Set(["Pass", "Pass with Condition"]);
      const cases = run.testCases || [];
      if (cases.length > 0) {
        const passed = cases.filter((tc) => passedStatuses.has(String(tc.runStatus || "").trim())).length;
        return Math.round((passed / cases.length) * 100);
      }
      return run.progress || 0;
    },
    statusPillClass(status) {
      const map = {
        Done: "pill-done",
        "In Progress": "pill-inprogress",
        "Not Started": "pill-inactive",
        Failed: "pill-failed",
        "Done without Deploy": "pill-done-without-deploy",
        Deployed: "pill-deployed",
      };
      return map[status] || "pill-inactive";
    },
    displayRunStatus(status) {
      const raw = String(status || "").trim();
      return raw === "Not Run" ? "No Run" : (raw || "—");
    },
    viewStatusColor(status) {
      const map = {
        "Not Started": "#000033",
        "Not Run": "#000033",
        "No Run": "#666699",
        "In Progress": "#483d8b",
        "In progress": "#483d8b",
        Done: "#2eb85c",
        Failed: "#e74c3c",
        "Done without Deploy": "#4d7c4d",
        Deployed: "#336633",
      };
      return map[String(status || "").trim()] || "#000033";
    },
    displayRunResult(result) {
      const map = {
        Fail: "Failed",
        Failed: "Failed",
        Pass: "Passed",
        "Pass with Condition": "Pass with Condition",
        Blocked: "Blocked",
        "No Run": "No Run",
        "Not Run": "No Run",
        "In Progress": "In progress",
        "In progress": "In progress",
      };
      const raw = String(result || "").trim();
      return map[raw] || raw || "No Run";
    },
    selectRun(run) {
      this.selectedRunId = run?.id || null;
    },
    toggleRunGroup(key) {
      const idx = this.collapsedRunGroups.indexOf(key);
      if (idx === -1) this.collapsedRunGroups.push(key);
      else this.collapsedRunGroups.splice(idx, 1);
    },
    runPlanCollapseKey(subKey, planKey) {
      return `${subKey}|${planKey}`;
    },
    isRunPlanCollapsed(subKey, planKey) {
      return this.collapsedRunPlans.includes(this.runPlanCollapseKey(subKey, planKey));
    },
    toggleRunPlan(subKey, planKey) {
      const key = this.runPlanCollapseKey(subKey, planKey);
      const idx = this.collapsedRunPlans.indexOf(key);
      if (idx === -1) this.collapsedRunPlans.push(key);
      else this.collapsedRunPlans.splice(idx, 1);
    },
    caseExecStatus(runStatus) {
      const raw = String(runStatus || "").trim();
      if (["Pass", "Fail", "Failed", "Pass with Condition", "Blocked"].includes(raw)) return "Done";
      if (raw === "In Progress" || raw === "In progress") return "In Progress";
      return "No Run";
    },
    caseExecStatusPill(runStatus) {
      const status = this.caseExecStatus(runStatus);
      return this.statusPillClass(status === "No Run" ? "Not Started" : status);
    },
    viewStepCount(tc) {
      const n = Array.isArray(tc?.steps) ? tc.steps.length : 0;
      return Math.max(n, 1);
    },
    viewExtraSteps(tc) {
      const steps = Array.isArray(tc?.steps) ? tc.steps : [];
      return steps.slice(1);
    },
    viewStepText(tc, idx) {
      const step = (tc?.steps || [])[idx];
      return step?.stepDetail || "";
    },
    viewExpectedText(tc, idx) {
      const step = (tc?.steps || [])[idx];
      return step?.expectedResult || "";
    },
    viewActualIsByStep(tc) {
      return isByStepMode(tc?.actualResultMode);
    },
    viewActualText(tc, idx) {
      if (this.viewActualIsByStep(tc)) {
        const step = (tc?.steps || [])[idx];
        const text = String(step?.actualResult || "").trim();
        return text || "—";
      }
      const text = String(tc?.actualResult || "").trim();
      return text || "—";
    },
    viewOverviewActual(tc) {
      const text = String(tc?.actualResult || "").trim();
      if (text) return text;
      const fromSteps = (tc?.steps || []).map((s) => String(s?.actualResult || "").trim()).filter(Boolean);
      return fromSteps.join("\n");
    },
    isVideoMediaUrl(url) {
      return isVideoMediaUrl(url);
    },
    viewOverviewImages(tc) {
      const overview = (tc?.actualResultImages || []).filter(Boolean);
      if (overview.length) return overview;
      return (tc?.steps || []).flatMap((s) => s.actualImages || []).filter(Boolean);
    },
    viewActualMediaPreview(tc) {
      return this.previewMediaItems(this.viewOverviewImages(tc));
    },
    viewStepActualImages(tc, idx) {
      if (this.viewActualIsByStep(tc)) {
        return ((tc?.steps || [])[idx]?.actualImages || []).filter(Boolean);
      }
      return this.viewOverviewImages(tc);
    },
    viewStepActualMediaPreview(tc, idx) {
      return this.previewMediaItems(this.viewStepActualImages(tc, idx));
    },
    previewMediaItems(all) {
      const items = Array.isArray(all) ? all.filter(Boolean) : [];
      if (!items.length) return [];
      if (items.length <= 3) {
        return items.map((src, index) => ({ src, index, extra: 0 }));
      }
      return [
        { src: items[0], index: 0, extra: 0 },
        { src: items[1], index: 1, extra: 0 },
        { src: items[2], index: 2, extra: items.length - 2 },
      ];
    },
    openViewStepMedia(tc, idx, index) {
      const items = this.viewStepActualImages(tc, idx);
      this.lightboxItems = items;
      this.lightboxIndex = Number.isFinite(index) ? index : 0;
      this.syncLightbox();
    },
    openViewMedia(tc, index) {
      const items = this.viewOverviewImages(tc);
      this.lightboxItems = items;
      this.lightboxIndex = Number.isFinite(index) ? index : 0;
      this.syncLightbox();
    },
    syncLightbox() {
      const src = this.lightboxItems[this.lightboxIndex] || null;
      this.lightboxSrc = src;
      this.lightboxIsVideo = isVideoMediaUrl(src);
    },
    lightboxPrev() {
      if (this.lightboxItems.length < 2) return;
      this.lightboxIndex = (this.lightboxIndex - 1 + this.lightboxItems.length) % this.lightboxItems.length;
      this.syncLightbox();
    },
    lightboxNext() {
      if (this.lightboxItems.length < 2) return;
      this.lightboxIndex = (this.lightboxIndex + 1) % this.lightboxItems.length;
      this.syncLightbox();
    },
    closeLightbox() {
      this.lightboxSrc = null;
      this.lightboxIsVideo = false;
      this.lightboxItems = [];
      this.lightboxIndex = 0;
    },
    groupRunViewDetails(details) {
      const rows = Array.isArray(details) ? details : [];
      const tcMap = {};
      rows.forEach((d) => {
        const key = d.testCaseId ? String(d.testCaseId) : `${d.scenario}|${d.title}`;
        if (!tcMap[key]) {
          const status = String(d.testCaseStatus || "").trim();
          tcMap[key] = {
            key,
            testCaseId: d.testCaseId,
            tcLabel: `TC-${String(d.testCaseNumber || d.testCaseId || 0).padStart(3, "0")}`,
            function: d.function || "",
            scenario: d.scenario || "",
            subScenario: d.subScenario || "",
            title: d.title ? String(d.title).trim() : "",
            testType: d.testType ? String(d.testType).trim() : "",
            typeOfTestCase: d.typeOfTestCase ? String(d.typeOfTestCase).trim() : "",
            priority: d.priority || "",
            preconditions: d.preconditions || "",
            testData: d.testData || "",
            runStatus: status === "Not Run" || !status ? "No Run" : status,
            actualResultMode: d.actualMode || "overview",
            actualResult: d.overviewActualDetail || "",
            actualResultImages: Array.isArray(d.overviewActualImages) ? d.overviewActualImages.filter(Boolean) : [],
            steps: [],
          };
        }
        if (d.overviewActualDetail && !tcMap[key].actualResult) {
          tcMap[key].actualResult = d.overviewActualDetail;
        }
        if (Array.isArray(d.overviewActualImages) && d.overviewActualImages.length && !tcMap[key].actualResultImages.length) {
          tcMap[key].actualResultImages = d.overviewActualImages.filter(Boolean);
        }
        if (d.actualMode) tcMap[key].actualResultMode = d.actualMode;
        if (d.stepNo != null) {
          tcMap[key].steps.push({
            stepNo: d.stepNo,
            platform: d.platform ? String(d.platform).trim() : "",
            stepDetail: d.stepDetail || "",
            expectedResult: d.expectedResult || "",
            actualResult: d.stepActualDetail || "",
            actualImages: Array.isArray(d.stepActualImages) ? d.stepActualImages.filter(Boolean) : [],
          });
        }
      });
      Object.values(tcMap).forEach((tc) => {
        tc.steps.sort((a, b) => (a.stepNo || 0) - (b.stepNo || 0));
      });
      return Object.values(tcMap);
    },
    setRunViewFilter(key, value) {
      this.runViewFilters = { ...this.runViewFilters, [key]: value ?? "" };
    },
    onRunViewScenarioFilterChange(value) {
      this.runViewFilters = { ...this.runViewFilters, scenario: value ?? "", subScenario: "" };
    },
    clearRunViewFilters() {
      this.runViewFilters = { search: "", testType: "", priority: "", scenario: "", subScenario: "", status: "" };
    },
    async openRunView() {
      const run = this.selectedRun;
      if (!run) return;
      this.viewingRun = run;
      this.showRunView = true;
      this.runViewMode = "standard";
      this.clearRunViewFilters();
      this.runViewLoading = true;
      this.runViewError = "";
      this.runViewCases = [];
      try {
        const details = await testRunDetailsApi.getByRun(run.id);
        this.runViewCases = this.groupRunViewDetails(details);
      } catch (e) {
        this.runViewError = e.message || "Failed to load test cases";
      } finally {
        this.runViewLoading = false;
      }
    },
    closeRunView() {
      this.unbindRunViewStickyHeaders();
      this.closeLightbox();
      this.showRunView = false;
      this.runViewCases = [];
      this.runViewError = "";
      this.viewingRun = null;
    },
    unbindRunViewStickyHeaders() {
      this._runViewStickyObserver?.disconnect();
      this._runViewStickyObserver = null;
    },
    syncRunViewStickyHeaders() {
      this.unbindRunViewStickyHeaders();
      if (!this.showRunView || this.runViewMode !== "pdf") return;
      this.$nextTick(() => {
        const table = document.querySelector(".qa-tr-view-overlay .qa-tc-pdf-table");
        if (!table) return;
        const update = () => {
          const thead = table.querySelector("thead");
          const sc = table.querySelector(".qa-tc-pdf-section--sc td");
          const thH = thead ? Math.ceil(thead.getBoundingClientRect().height) : 40;
          const scH = sc ? Math.ceil(sc.getBoundingClientRect().height) : 28;
          table.style.setProperty("--qa-tc-pdf-th", `${thH}px`);
          table.style.setProperty("--qa-tc-pdf-sc", `${scH}px`);
        };
        update();
        if (typeof ResizeObserver === "undefined") return;
        this._runViewStickyObserver = new ResizeObserver(update);
        this._runViewStickyObserver.observe(table);
        const thead = table.querySelector("thead");
        if (thead) this._runViewStickyObserver.observe(thead);
      });
    },
    viewResultColor(status) {
      const map = {
        "No Run": "#666699",
        "Not Run": "#000033",
        Fail: "#FF0000",
        Failed: "#FF0000",
        Pass: "#006400",
        Passed: "#006400",
        "Pass with Condition": "#006400",
        Blocked: "#fb6340",
        "In progress": "#000080",
        "In Progress": "#000080",
      };
      return map[String(status || "").trim()] || "#666699";
    },
    viewResultChipClass(status) {
      const map = {
        "No Run": "is-no-run",
        "Not Run": "is-not-run",
        Fail: "is-fail",
        Failed: "is-fail",
        Pass: "is-pass",
        Passed: "is-pass",
        "Pass with Condition": "is-pass",
        Blocked: "is-blocked",
        "In progress": "is-in-progress",
        "In Progress": "is-in-progress",
      };
      return map[String(status || "").trim()] || "is-no-run";
    },
    reviewTypeToneClass(testType) {
      const v = String(testType || "").trim().toLowerCase();
      if (v === "positive") return "is-positive";
      if (v === "negative") return "is-negative";
      return "";
    },
    reviewPriorityToneClass(priority) {
      const v = String(priority || "").trim().toLowerCase();
      if (v === "high" || v === "critical") return "is-high";
      if (v === "medium") return "is-medium";
      if (v === "low") return "is-low";
      return "";
    },
    enumLabel(value) {
      const raw = String(value || "").trim();
      if (!raw) return "—";
      return raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    resultPillClass(result) {
      const map = {
        Pass: "pill-done",
        "Pass with Condition": "pill-done",
        Fail: "pill-failed",
        Failed: "pill-failed",
        Blocked: "pill-block",
        "No Run": "pill-inactive",
        "Not Run": "pill-inactive",
        "In Progress": "pill-inprogress",
        "In progress": "pill-inprogress",
      };
      return map[String(result || "").trim()] || "pill-inactive";
    },
    assigneeList(run) {
      if (!run.assignee) return [];
      if (Array.isArray(run.assignee)) return run.assignee.map((name) => this.formatAssignedName(name));
      return [this.formatAssignedName(run.assignee)];
    },
    progressColor(pct) {
      if (pct >= 100) return "success";
      if (pct >= 50) return "info";
      if (pct > 0) return "warning";
      return "secondary";
    },
  },
};
</script>

<style scoped>
/* Table layout */
.history-filter-field {
  width: 160px;
  min-width: 140px;
}
.run-table { border: 1px solid #e9ecef; border-radius: 10px; overflow: hidden; }
.run-table-header,
.run-row {
  display: grid;
  /* ปรับตัวเลข fr ของแต่ละคอลัมน์ได้ — รวมกันจะเฉลี่ยเต็มตาราง */
  grid-template-columns:
    minmax(0, 2.5fr)    /* 1 TEST RUN NAME */
    minmax(0, 0.6fr)    /* 2 ENVIRONMENT */
    minmax(0, 0.6fr)    /* 3 START DATE */
    minmax(0, 0.6fr)    /* 4 DUE DATE */
    minmax(0, 0.3fr)  /* 5 TOTAL */
    minmax(0, 0.8fr)  /* 6 STATUS */
    minmax(0, 0.8fr)  /* 7 PROGRESS */
    minmax(65px, 0.8fr); /* 8 ACTIONS */
  align-items: center;
  column-gap: 0.35rem;
}
.run-table-header {
  padding: 0.65rem 1rem;
  background: #f8f9fa; border-bottom: 1px solid #e9ecef;
  font-size: 0.7rem; font-weight: 700; color: #8392ab;
  text-transform: uppercase; letter-spacing: 0.04em;
}
.run-row {
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #f1f3f5; transition: background 0.15s;
}
.run-row:last-child { border-bottom: none; }
.run-row:hover { background: #fafbfc; }
.run-table-header .col-stage,
.run-table-header .col-result,
.run-row .col-stage,
.run-row .col-result {
  display: none !important;
}
.col-name     { min-width: 0; }
.col-env,
.col-date,
.col-total,
.col-status,
.col-progress,
.col-action { min-width: 65px; }
.run-table-header .col-status,
.run-row .col-status {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.run-row--selected { background: #f0f4ff; }
.run-row--selected:hover { background: #e8eefc; }
.run-folder-row {
  display: flex;
  align-items: center;
  min-width: 0;
  width: 100%;
  cursor: pointer;
}
.run-folder-chevron {
  width: 12px;
  font-size: 0.65rem;
  color: #8392ab;
  text-align: center;
}
.run-folder-icon--sub-project {
  color: #5e72e4;
}
.run-folder-icon--plan {
  color: #fb8c00;
}
.run-folder-row--sub-project {
  padding: 0.6rem 1rem;
  background: #e8edf5;
  border-bottom: 1px solid #dde2eb;
  font-size: 0.75rem;
  font-weight: 700;
  color: #344767;
}
.run-folder-row--plan {
  padding: 0.55rem 1rem 0.55rem 2rem;
  background: #f3f4f6;
  border-bottom: 1px solid #e9ecef;
  border-top: 1px solid #e2e5e9;
  font-size: 0.75rem;
  font-weight: 700;
  color: #344767;
}
.run-folder-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.run-folder-count {
  flex-shrink: 0;
  margin-left: 0.35rem;
  font-size: 0.72rem;
  font-weight: 700;
  color: #67748e;
}

.run-name-line {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  width: 100%;
}
.run-name-btn {
  background: transparent; border: 0; padding: 0;
  font-size: 0.875rem; color: #344767; cursor: pointer; font-weight: 700; text-align: left;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.run-name-btn:hover { color: #5e72e4; text-decoration: underline; }
.run-overdue-label {
  flex-shrink: 0;
  margin-left: auto;
  color: #ff0000;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
  line-height: 1;
}
.run-sub { font-size: 0.78rem; color: #8392ab; margin-top: 2px; }

/* QA badges */
.qa-badge {
  display: inline-block; background: #e8f4fd; color: #0c7a9a;
  border: 1px solid #bee5eb; border-radius: 50px;
  font-size: 0.72rem; font-weight: 600; padding: 0.15rem 0.55rem;
}

.bo-assignee-pill {
  background: #e8f4fd !important;
  color: #0c7a9a !important;
  border-color: #bee5eb !important;
}
.config-badge {
  display: inline-block; background: #f0f0f0; color: #8392ab;
  border: 1px solid #e0e0e0; border-radius: 50px;
  font-size: 0.72rem; font-weight: 600; padding: 0.15rem 0.55rem;
}

/* Status pills */
.status-pill {
  display: inline-block; padding: 0.25rem 0.75rem;
  border-radius: 50px; font-size: 0.75rem; font-weight: 600;
}
.pill-done       { background: #d4f5e2; color: #28a745; border: 1px solid #b7efd0; }
.pill-inprogress { background: #d1ecf1; color: #0c7a9a; border: 1px solid #bee5eb; }
.pill-inactive   { background: #f0f0f0; color: #8392ab; border: 1px solid #e0e0e0; }
.pill-failed     { background: #fde8e8; color: #e74c3c; border: 1px solid #f9c0c0; }

/* Action buttons */
.run-row .col-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
}
.run-row .col-action .btn-link {
  flex-shrink: 0;
  line-height: 1;
  font-size: 0.95rem;
}
.run-row .col-action .btn-link i {
  font-size: 0.95rem;
}
.action-edit   { color: #4361ee; }
.action-edit:hover { color: #2d4bc8; }
.action-delete { color: #e74c3c; }
.action-delete:hover { color: #c0392b; }
.run-result-pill--condition {
  font-size: 0.62rem !important;
  white-space: nowrap;
}

/* Summary cards */
.summary-card {
  background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 8px;
  padding: 0.65rem 0.85rem; pointer-events: none; user-select: none;
}
.summary-card-editable {
  pointer-events: auto; user-select: auto;
}
.summary-date-input {
  font-size: 0.8rem; font-weight: 600; color: #344767;
  border-color: #e9ecef; background: #fff;
}
.summary-label {
  font-size: 0.68rem; font-weight: 700; color: #8392ab;
  text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 0.15rem;
}
.summary-hint { font-size: 0.62rem; font-weight: 400; text-transform: none; letter-spacing: 0; }
.summary-value { font-size: 0.95rem; font-weight: 700; color: #344767; margin-bottom: 0; }

/* Selection section */
.selection-section {
  border: 1px solid #e9ecef; border-radius: 8px; padding: 0.85rem 1rem;
  background: #fafbfc;
}
.selection-section--error {
  border-color: #ef5350;
}
.test-run-modal .form-control.is-invalid {
  border-color: #ef5350;
}
.sel-box-title {
  font-size: 0.72rem; font-weight: 700; color: #344767;
  text-transform: uppercase; letter-spacing: 0.04em;
  margin-bottom: 0.6rem;
}

/* Selection table */
.sel-header {
  display: flex; align-items: center; gap: 6px; padding: 0.45rem 0.5rem;
  background: #f1f3f5; border-radius: 6px; margin-bottom: 4px;
  font-size: 0.68rem; font-weight: 700; color: #8392ab;
  text-transform: uppercase; letter-spacing: 0.03em;
}
.sel-row {
  display: flex; align-items: center; gap: 6px;
  padding: 0.35rem 0.5rem; border-bottom: 1px solid #f1f3f5;
}
.sel-row:last-child { border-bottom: none; }
/* By Scenario flex columns (total=11.5, fixed=327px) */
.sc-col-1 { flex: 3.5; min-width: 0; }
.sc-col-2 { flex: 8;   min-width: 0; }

/* By Test Case flex columns (total=12, fixed=272px) */
/* total 12 ชดเชย fixed ที่น้อยกว่า 55px → Scenario col ≈ เท่ากัน */
.tc-col-1 { flex: 3.5; min-width: 0; }
.tc-col-2 { flex: 3.5; min-width: 0; }
.tc-col-3 { flex: 5;   min-width: 0; }

/* Shared fixed-width columns */
.sel-col-date  { flex: 0 0 120px; width: 120px; }
.sel-col-count { flex: 0 0 55px;  width: 55px; }
.sel-col-del   { flex: 0 0 32px;  width: 32px; }
.sel-center    { display: flex; align-items: center; justify-content: center; }

/* Modal */
.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1050;
  display: flex; align-items: center; justify-content: center; padding: 1rem;
}
.modal-box { background: #fff; border-radius: 12px; box-shadow: 0 20px 60px rgba(0,0,0,0.2); }
:global(body:has(.modal-overlay) #sidenav-main) { z-index: 1 !important; }
.jira-multiselect { position: relative; }
.jira-selector {
  display: flex; align-items: center; flex-wrap: wrap; gap: 4px;
  min-height: 32px; padding: 4px 8px; border: 1px solid #dee2e6;
  border-radius: 6px; cursor: pointer; background: #fff; font-size: 0.8rem;
}
.jira-selector:hover { border-color: #adb5bd; }
.jira-badge {
  display: inline-flex; align-items: center; gap: 3px;
  background: #e0f0ff; color: #0c63e4; border: 1px solid #b6d4fe;
  border-radius: 4px; padding: 1px 6px; font-size: 0.75rem; font-weight: 600;
}
.jira-badge-remove { cursor: pointer; font-weight: bold; opacity: 0.7; }
.jira-badge-remove:hover { opacity: 1; }
.jira-dropdown-menu {
  position: absolute; top: calc(100% + 2px); left: 0; right: 0; z-index: 9999;
  background: #fff; border: 1px solid #dee2e6; border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.12); max-height: 180px; overflow-y: auto;
}
.jira-dropdown-item {
  display: flex; align-items: center; padding: 6px 10px; cursor: pointer; font-size: 0.8rem;
}
.jira-dropdown-item:hover { background: #f0f4ff; }
</style>

<style>
/* Unscoped: Teleport to body — fixed header/footer, scroll body only */
.modal-overlay.test-run-modal-overlay {
  overflow: hidden;
}

.test-run-modal.modal-box {
  width: 1400px;
  max-width: 98vw;
  height: calc(100vh - 2rem);
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.test-run-modal-header {
  flex: 0 0 auto;
  background: #fff;
  z-index: 2;
}

.test-run-modal-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

.test-run-modal-footer {
  flex: 0 0 auto;
  background: #fff;
  z-index: 2;
}

.qa-tr-view-overlay {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  z-index: 5000;
  background: #fff;
}
.qa-tr-view-overlay .qa-tc-review-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.qa-tr-view-overlay .qa-tc-review-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1.25rem;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
}
.qa-tr-view-overlay .qa-tc-review-filters {
  position: relative;
  z-index: 40;
  overflow: visible;
  display: flex !important;
  flex-wrap: nowrap;
  align-items: flex-end;
  gap: 0.75rem;
}
.qa-tr-view-overlay .qa-tc-review-filter-field {
  overflow: visible;
  min-width: 0;
  flex: 1 1 0;
}
.qa-tr-view-overlay .qa-tc-review-filter-field--search {
  flex: 1.3 1 0;
  grid-column: auto !important;
}
.qa-tr-view-overlay .qa-tc-review-filter-field .base-select--open {
  z-index: 50;
}
.qa-tr-view-overlay .qa-tc-review-filter-field--clear {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}
.qa-tr-view-overlay .qa-tr-view-clear-btn {
  white-space: nowrap;
  height: 32px;
}
.qa-tr-view-overlay .qa-tc-review-body {
  flex: 1;
  min-height: 0;
  display: flex;
  position: relative;
  z-index: 0;
}
.qa-tr-view-overlay .qa-tc-review-doc {
  flex: 1;
  min-width: 0;
  overflow: auto;
  padding: 1.25rem;
}
.qa-tr-view-overlay .qa-tc-review-doc-title,
.qa-tr-view-overlay .qa-tc-review-title-block > p:first-child {
  display: none !important;
}
.qa-tr-view-overlay .qa-tc-review-title-block {
  margin-bottom: 1.25rem;
}
.qa-tr-view-overlay .qa-tc-review-info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  background: #fafafa;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  padding: 0.75rem 0.9rem;
}
.qa-tr-view-overlay .qa-tc-review-info-grid span {
  display: block;
  font-size: 0.7rem;
  color: #8392ab;
}
.qa-tr-view-overlay .qa-tc-review-info-grid strong {
  display: block;
  font-size: 0.82rem;
  color: #344767;
  margin-top: 0.15rem;
}
.qa-tr-view-overlay .qa-tr-view-result {
  font-weight: 700;
}
.qa-tr-view-overlay .qa-tc-review-mode {
  display: inline-flex;
  align-items: center;
  padding: 3px;
  border: 1px solid #dce1ea;
  border-radius: 8px;
  background: #f8f9fb;
}
.qa-tr-view-overlay .qa-tc-review-mode-btn {
  border: 0;
  background: transparent;
  color: #67748e;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.42rem 0.7rem;
  border-radius: 6px;
  cursor: pointer;
}
.qa-tr-view-overlay .qa-tc-review-mode-btn.active {
  background: #fff;
  color: #344767;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
}
.qa-tr-view-overlay .qa-tc-pdf-table {
  width: 100%;
  table-layout: fixed;
  font-size: 0.78rem;
  border-collapse: separate;
  border-spacing: 0;
}
.qa-tr-view-overlay .qa-tc-pdf-table thead th {
  position: sticky;
  top: 0;
  z-index: 8;
  background: #344767;
  background-clip: padding-box;
  color: #fff;
  font-weight: 700;
  font-size: 0.72rem;
  text-align: center;
  white-space: nowrap;
  vertical-align: middle;
}
.qa-tr-view-overlay .qa-tc-pdf-table .qa-tc-pdf-section--sc td {
  position: sticky;
  top: var(--qa-tc-pdf-th, 2.5rem);
  z-index: 7;
  background: #f5f5f5;
}
.qa-tr-view-overlay .qa-tc-pdf-table .qa-tc-pdf-section--sub td {
  position: sticky;
  top: calc(var(--qa-tc-pdf-th, 2.5rem) + var(--qa-tc-pdf-sc, 1.85rem));
  z-index: 6;
  background: #fff;
}
.qa-tr-view-col-id { width: 70px; }
.qa-tr-view-col-name { width: 180px; }
.qa-tr-view-col-type { width: 70px; }
.qa-tr-view-col-priority { width: 70px; }
.qa-tr-view-col-step { width: 180px; }
.qa-tr-view-col-expected { width: 180px; }
.qa-tr-view-col-actual { width: 180px; }
.qa-tr-view-col-result { width: 80px; }
.qa-tr-view-overlay .qa-tc-review-case {
  border: 1px solid #dcdcdc;
  border-radius: 10px;
  padding: 0.85rem;
  margin-bottom: 0.75rem;
}
.qa-tr-view-overlay .qa-tc-review-case-top {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: flex-start;
}
.qa-tr-view-overlay .qa-tc-review-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 0.7rem;
}
.qa-tr-view-overlay .qa-tc-review-label {
  font-size: 0.68rem;
  font-weight: 800;
  color: #212529;
  text-transform: uppercase;
  margin-bottom: 0.2rem;
}
.qa-tr-view-overlay .qa-tc-review-value {
  font-size: 0.8rem;
  color: rgb(18, 56, 94);
  white-space: pre-wrap;
  margin: 0;
}
.qa-tr-view-overlay .qa-tc-review-steps {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 0.78rem;
}
.qa-tr-view-overlay .qa-tc-review-steps th,
.qa-tr-view-overlay .qa-tc-review-steps td {
  border: 1px solid #dcdcdc;
  padding: 0.4rem 0.5rem;
  vertical-align: top;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.qa-tr-view-overlay .qa-tc-review-steps th {
  background: #344767;
  color: #fff;
  font-weight: 700;
}
.qa-tr-view-overlay .qa-tc-review-steps .qa-tc-review-col-no { width: 50px; }
.qa-tr-view-overlay .qa-tc-review-steps .qa-tc-review-col-module { width: 80px; }
.qa-tr-view-overlay .qa-tc-review-steps .qa-tc-review-col-step,
.qa-tr-view-overlay .qa-tc-review-steps .qa-tc-review-col-expected,
.qa-tr-view-overlay .qa-tc-review-steps .qa-tc-review-col-actual {
  width: calc((100% - 130px) / 3);
}
.qa-tr-view-overlay .qa-tr-view-result-head {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
  padding-top: 0.1rem;
}
.qa-tr-view-overlay .qa-tr-view-result-head .qa-tc-review-k {
  font-size: 0.72rem;
  font-weight: 700;
}
.qa-tr-view-overlay .qa-tr-view-result-chip {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.2rem 0.55rem;
  border-radius: 0.375rem;
  border: 1px solid;
  white-space: nowrap;
  min-width: 120px;
  text-align: center;
  box-sizing: border-box;
}
.qa-tr-view-overlay .qa-tr-view-result-chip.is-pass-condition {
  font-size: 0.68rem;
}
.qa-tr-view-overlay .qa-tr-view-result-chip.is-no-run {
  color: #666699;
  border-color: #666699;
  background: #fff;
}
.qa-tr-view-overlay .qa-tr-view-result-chip.is-not-run {
  color: #000033;
  border-color: #000033;
  background: #f5f5f5;
}
.qa-tr-view-overlay .qa-tr-view-result-chip.is-pass {
  color: #006400;
  border-color: #006400;
  background: #e6fae6;
}
.qa-tr-view-overlay .qa-tr-view-result-chip.is-fail {
  color: #ff0000;
  border-color: #ff0000;
  background: #fff0f5;
}
.qa-tr-view-overlay .qa-tr-view-result-chip.is-blocked {
  color: #fb6340;
  border-color: #fb6340;
  background: #fff5ee;
}
.qa-tr-view-overlay .qa-tr-view-result-chip.is-in-progress {
  color: #000080;
  border-color: #000080;
  background: #f0ffff;
}
.qa-tr-view-overlay .qa-tr-view-actual-field {
  grid-template-columns: 1fr;
  margin-top: 0.25rem;
}
.qa-tr-view-overlay .qa-tr-view-media {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.4rem;
}
.qa-tr-view-overlay .qa-tr-view-media-thumb {
  position: relative;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 1px solid #dce1ea;
  border-radius: 6px;
  overflow: hidden;
  background: #f1f3f5;
  cursor: zoom-in;
}
.qa-tr-view-overlay .qa-tr-view-media-thumb img,
.qa-tr-view-overlay .qa-tr-view-media-thumb video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.qa-tr-view-overlay .qa-tr-view-media-play,
.qa-tr-view-overlay .qa-tr-view-media-more {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  background: rgba(15, 23, 42, 0.42);
}
.qa-tr-view-overlay .qa-tr-view-media-play {
  font-size: 0.55rem;
}
.lightbox-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.85);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999;
}
.lightbox-img {
  max-width: 90vw; max-height: 90vh;
  border-radius: 8px; object-fit: contain;
  box-shadow: 0 8px 40px rgba(0,0,0,0.5);
}
.lightbox-close {
  position: absolute; top: 16px; right: 24px;
  background: none; border: none; color: white;
  font-size: 2rem; cursor: pointer; line-height: 1;
  z-index: 2;
}
.lightbox-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  cursor: pointer;
  z-index: 2;
}
.lightbox-prev { left: 18px; }
.lightbox-next { right: 18px; }
.lightbox-count {
  position: absolute;
  bottom: 18px;
  left: 50%;
  transform: translateX(-50%);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
}
</style>
